// src/components/Reports.js
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import { createApiClient } from "../utils/apiClient";
import { Roles } from "../utils/access";
import { getPdfServerUrl } from "../utils/pdfServer";
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";
import type { ApiError } from "../types/UserTypes";
import type { components } from "../types/api";

import { ReportRenderer } from "../reporting/renderer/ReportRenderer";
import { exportToExcel, buildExportFileName } from "../reporting/lib/excelExport";
import {
  toFormattedInvoicePayload,
  type FormattedInvoicePayload,
} from "../reporting/formattedInvoice/formattedInvoiceData";
import ReportsV3Preview, { hasReportsV3PreviewData } from "../reporting/reportsV3/ReportsV3Preview";
import { nativeWorkbookSupport } from "../reporting/reportsV3/nativeLayout.mjs";
import { assertReportAdmission } from "../reporting/reportsV3/nativeConditions.mjs";
import { exportNativeExcel } from "../reporting/reportsV3/nativeExcelExport";
import { buildRenderContext } from "../reporting/reportsV3/renderOperation";
import { isReportAccessDenied, normalizeReportAudience, reportFailureMessage } from "../reporting/reportsV3/reportAccess";
import { getSystemReportHelpUrl } from "../utils/helpTopics";

// Templates
import invoiceTemplate from "../reporting/examples/invoice-report-template-v1.json";
import outstandingTemplate from "../reporting/examples/outstanding-invoice-report-template-v1.json";
import myInvoiceTemplate from "../reporting/examples/my-invoice-report-template-v1.json";
import landmanTemplate from "../reporting/examples/landman-report-template-v1.json";
import landmanPaymentSummaryTemplate from "../reporting/examples/landman-payment-summary-report-template-v1.json";
import landmanStatusTemplate from "../reporting/examples/landman-status-report-template-v1.json";

// CSS
import "../reporting/styles.css";
import "../reporting/examples/invoice-report-style-v1.css";
import "../reporting/examples/outstanding-invoice-report-style-v1.css";
import "../reporting/examples/my-invoice-report-style-v1.css";
import "../reporting/examples/landman-report-style-v1.css";
import "../reporting/examples/landman-payment-summary-report-style-v1.css";
import "../reporting/examples/landman-status-report-style-v1.css";
import cssStyles from "./Reports.module.css";

// Demo fallback
import invoiceDemo from "../reporting/examples/invoice-report-data-v1.json";
import outstandingDemo from "../reporting/examples/outstanding-invoice-report-data-v1.json";
import myInvoiceDemo from "../reporting/examples/my-invoice-report-data-v1.json";
import landmanDemo from "../reporting/examples/landman-report-data-v1.json";
import landmanPaymentSummaryDemo from "../reporting/examples/landman-payment-summary-report-data-v1.json";
import landmanStatusDemo from "../reporting/examples/landman-status-report-data-v1.json";
import {
  buildLandmanPaymentSummaryData,
  normalizeLandmanPayees,
} from "../reporting/lib/landmanSummary";

const REPORTS_CALENDAR_VIEWPORT_PADDING = 12;
const REPORTS_CALENDAR_DEFAULT_WIDTH = 260;
const RECENT_INVOICE_TAKE = 5;

const INVOICE_SELECTION_REPORT_IDS = new Set([
  "invoice",
  "outstanding",
]);
const EXISTING_INVOICE_REQUIRED_REPORT_IDS = new Set(["outstanding"]);
const INVOICE_DELETE_REPORT_IDS = new Set(["invoice", "outstanding"]);

type BillingPeriodRange = {
  startDate?: string | null;
  endDate?: string | null;
  StartDate?: string | null;
  EndDate?: string | null;
};

type BillingPeriodResponse = {
  clientId?: number;
  billingPeriodMode?: string | null;
  BillingPeriodMode?: string | null;
  currentPeriod?: BillingPeriodRange | null;
  previousPeriod?: BillingPeriodRange | null;
  nextPeriod?: BillingPeriodRange | null;
  CurrentPeriod?: BillingPeriodRange | null;
  PreviousPeriod?: BillingPeriodRange | null;
  NextPeriod?: BillingPeriodRange | null;
};

type InvoiceCustomField = components["schemas"]["InvoiceCustomFieldModel"];
type InvoiceReportSettings = components["schemas"]["InvoiceReportSettingsModel"];
type InvoiceReportOptions = components["schemas"]["InvoiceReportOptionsModel"];
type SuggestedInvoiceCustomField = components["schemas"]["SuggestedInvoiceCustomFieldModel"];
type ReportDefinitionSummaryV3 = components["schemas"]["ReportDefinitionSummaryV3"];
type ReportExecuteRequestV3 = components["schemas"]["ReportExecuteRequestV3"];
type ReportExecuteResponseV3 = components["schemas"]["ReportExecuteResponseV3"];
type InvoiceCustomFieldPlacement = InvoiceCustomField["placement"];
type InvoiceCustomFieldDraft = {
  uid: string;
  id?: number;
  label: string;
  value: string;
  placement: InvoiceCustomFieldPlacement;
  sortOrder?: number;
};

const CUSTOM_FIELD_PLACEMENTS: Array<{ value: InvoiceCustomFieldPlacement; label: string }> = [
  { value: "header", label: "Header" },
  { value: "summary", label: "Summary" },
];
const MAX_INVOICE_CUSTOM_FIELDS = 25;
const MAX_INVOICE_CUSTOM_FIELD_LABEL_LENGTH = 100;
const MAX_INVOICE_CUSTOM_FIELD_VALUE_LENGTH = 500;
let invoiceCustomFieldDraftUid = 0;
const DEFAULT_INVOICE_REPORT_OPTIONS: Required<InvoiceReportOptions> = {
  splitSummariesByCounty: false,
};

const REPORTS = [
  {
    id: "timeEntryDetails",
    label: "Time Entry Details",
    helpText: "Detailed breakdown of all time entries for a client within a date range.",
    template: landmanStatusTemplate,
    demoData: landmanStatusDemo,
    requiresClient: true,
    requiresDateRange: true,
    roles: [Roles.Owner, Roles.Admin],
    freeCrewOnly: true,
  },
  {
    id: "invoice",
    label: "Invoice Report",
    helpText: "Generate a client-facing invoice from approved time entries.",
    template: invoiceTemplate,
    demoData: invoiceDemo,
    requiresClient: true,
    requiresDateRange: true,
    roles: [Roles.Owner, Roles.Admin],
    feature: "invoice_generation",
  },
  {
    id: "outstanding",
    label: "Outstanding Invoice Report",
    helpText: "View outstanding balances on a previously generated invoice.",
    template: outstandingTemplate,
    demoData: outstandingDemo,
    requiresClient: true,
    requiresDateRange: true,
    roles: [Roles.Owner, Roles.Admin],
    feature: "invoice_generation",
  },
  {
    id: "myInvoice",
    label: "My Invoice Report",
    helpText: "Your personal invoice summary for a date range.",
    template: myInvoiceTemplate,
    demoData: myInvoiceDemo,
    requiresClient: false,
    requiresDateRange: true,
    roles: [Roles.Owner, Roles.Admin, Roles.ProjectManager, Roles.User],
    feature: "invoice_generation",
  },
  {
    id: "landman",
    label: "Landman Report",
    helpText: "Date-range landman report for invoiced time entries only.",
    template: landmanTemplate,
    demoData: landmanDemo,
    requiresClient: false,
    requiresDateRange: true,
    roles: [Roles.Owner, Roles.Admin],
    feature: "invoice_generation",
  },
  {
    id: "landmanPaymentSummary",
    label: "Landman Payment Summary",
    helpText: "Date-range landman payment summary for invoiced time entries only.",
    template: landmanPaymentSummaryTemplate,
    demoData: landmanPaymentSummaryDemo,
    requiresClient: false,
    requiresDateRange: true,
    roles: [Roles.Owner, Roles.Admin],
    feature: "invoice_generation",
  },
  {
    id: "landmanStatus",
    label: "Landman Status Report",
    helpText: "Status report showing all time entries with approval status.",
    template: landmanStatusTemplate,
    demoData: landmanStatusDemo,
    requiresClient: true,
    requiresDateRange: true,
    roles: [Roles.Owner, Roles.Admin],
    feature: "invoice_generation",
  },
];

function isApiError(error: unknown): error is ApiError {
  return Boolean(error && typeof error === "object" && "status" in error);
}

function isNotFoundApiError(error: unknown): error is ApiError {
  return isApiError(error) && Number(error.status) === 404;
}

function reportErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error) && error.code === "NO_ELIGIBLE_INVOICE_ROWS") {
    return error.message || "No approved billable time entries are available for this invoice date range.";
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

function reportResultHasRenderableData(data: unknown) {
  const source = data && typeof data === "object" ? data : {};
  return Object.values(source).some((value: any) => (
    Array.isArray(value) && value.length > 0
  ));
}

function invoicePreviewHasUninvoicedTime(preview: any) {
  const arrayFields = [
    preview?.brokers,
    preview?.Brokers,
    preview?.summaryRows,
    preview?.SummaryRows,
    preview?.landmen,
    preview?.Landmen,
  ];
  if (arrayFields.some((field) => Array.isArray(field) && field.length > 0)) {
    return true;
  }

  const amountDue = Number(
    preview?.totalsBox?.amountDue ??
      preview?.totalsBox?.AmountDue ??
      preview?.TotalsBox?.amountDue ??
      preview?.TotalsBox?.AmountDue ??
      0,
  );
  return Number.isFinite(amountDue) && amountDue > 0;
}

function usesInvoiceSelection(reportId: any) {
  return INVOICE_SELECTION_REPORT_IDS.has(String(reportId || ""));
}

function requiresExistingInvoiceSelection(reportId: any) {
  return EXISTING_INVOICE_REQUIRED_REPORT_IDS.has(String(reportId || ""));
}

function runsFromToolbar(reportId: any) {
  return !requiresExistingInvoiceSelection(reportId);
}

function canRunUninvoicedDateRange(reportId: any) {
  return reportId === "invoice";
}

function getToolbarActionLabel(reportId: any, hasInvoiceList: boolean) {
  return requiresExistingInvoiceSelection(reportId)
    ? hasInvoiceList
      ? "Refresh Invoices"
      : "Find Invoices"
    : "Run";
}

function isReportsV3Report(report: any) {
  return report?.source === "reportsV3";
}

const LANDMAN_INCLUDE_REPORT_KEYS = new Set(["landman", "landmanPaymentSummary", "landmanMonthlyStatement"]);

function requireEffectiveIncludeSetting(metadata: Record<string, unknown>, requested: boolean): boolean {
  const effective = metadata.invoicedOnly;
  if (typeof effective !== "boolean" || effective !== requested) {
    throw new Error("The report service did not confirm the requested Include setting. Update the report service and run the report again.");
  }
  return effective;
}

function metadataString(source: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function metadataBoolean(source: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "boolean") return value;
  }
  return undefined;
}

function normalizeReportRoles(value: unknown) {
  return normalizeReportAudience(value);
}

function getReportKey(report: any) {
  return String(report?.reportKey || report?.id || "");
}

function getReportScope(report: any) {
  return String(report?.scope || "").toLowerCase();
}

function isTenantScopedReport(report: any) {
  return getReportScope(report) === "tenant" || Boolean(report?.tenantId);
}

function getReportOptionLabel(report: any) {
  return report?.engine === "v2" ? `${report.label} (V2)` : report?.label;
}

function buildReportsV3ReportDefinition(definition: ReportDefinitionSummaryV3) {
  const definitionId = definition.definitionId || "";
  if (!definitionId) return null;
  const definitionMeta = definition as Record<string, unknown>;
  const description = typeof definitionMeta.description === "string" ? definitionMeta.description : "";
  const reportKey = metadataString(definitionMeta, ["reportKey", "reportType", "legacyReportId"]) || definitionId;
  const scope = metadataString(definitionMeta, ["scope"]) || (definitionMeta.tenantId ? "tenant" : "system");
  const requiredFilters = (
    typeof definitionMeta.requiredFilters === "object" && definitionMeta.requiredFilters !== null
      ? definitionMeta.requiredFilters
      : {}
  ) as Record<string, unknown>;
  const tenantScoped = scope.toLowerCase() === "tenant" || Boolean(definitionMeta.tenantId);
  const explicitCustomFeature = metadataString(definitionMeta, ["customFeature"]);
  const customFeature = tenantScoped
    ? explicitCustomFeature || "custom_report_templates"
    : explicitCustomFeature && explicitCustomFeature !== "custom_report_templates"
      ? explicitCustomFeature
      : undefined;

  return {
    id: definitionId,
    definitionId,
    revision: definition.revision,
    reportKey,
    scope,
    tenantId: definitionMeta.tenantId,
    overrideOf: metadataString(definitionMeta, ["overrideOf", "replacesReportKey"]),
    label: definition.name || "Custom Report",
    helpText: description,
    requiresClient: metadataBoolean(requiredFilters, ["client", "clientId", "requiresClient"]) ?? true,
    requiresDateRange: metadataBoolean(requiredFilters, ["dateRange", "requiresDateRange"]) ?? true,
    roles: normalizeReportRoles(definitionMeta.roles),
    feature: metadataString(definitionMeta, ["requiredFeature"]) || undefined,
    customFeature,
    freeCrewOnly: Boolean(definitionMeta.freeCrewOnly),
    source: "reportsV3",
  };
}

function resolveReportsV3Definitions(reports: any[]) {
  const byReportKey = new Map<string, any>();
  for (const report of reports) {
    const reportKey = getReportKey(report);
    if (!reportKey) continue;
    const existing = byReportKey.get(reportKey);
    if (!existing || (isTenantScopedReport(report) && !isTenantScopedReport(existing))) {
      byReportKey.set(reportKey, report);
    }
  }
  return [...byReportKey.values()];
}

function isInvoicePreviewMeta(meta: any) {
  return meta?.reportId === "invoice" && Number(meta?.invoiceId || 0) <= 0;
}

function isGeneratedInvoiceMeta(meta: any) {
  return meta?.reportId === "invoice" && Number(meta?.invoiceId || 0) > 0;
}

function canDeleteSelectedInvoice(reportId: any) {
  return INVOICE_DELETE_REPORT_IDS.has(String(reportId || ""));
}

function getInvoiceDocId(doc: any) {
  return Number(
    doc?.invoiceId ??
      doc?.InvoiceId ??
      doc?.outstandingInvoiceId ??
      doc?.OutstandingInvoiceId ??
      doc?.id ??
      doc?.Id,
  ) || 0;
}

function getInvoiceDocName(doc: any) {
  return String(
    doc?.invoiceName ??
      doc?.InvoiceName ??
      doc?.reportName ??
      doc?.ReportName ??
      "(unnamed)",
  );
}

function normalizeInvoiceDoc(doc: any) {
  return {
    ...doc,
    id: getInvoiceDocId(doc),
    invoiceId: Number(doc?.invoiceId ?? doc?.InvoiceId ?? 0) || undefined,
    outstandingInvoiceId: Number(doc?.outstandingInvoiceId ?? doc?.OutstandingInvoiceId ?? 0) || undefined,
    invoiceName: getInvoiceDocName(doc),
    startDate: doc?.startDate ?? doc?.StartDate ?? null,
    endDate: doc?.endDate ?? doc?.EndDate ?? null,
    generatedDate: doc?.generatedDate ?? doc?.GeneratedDate ?? null,
    periodCovered: doc?.periodCovered ?? doc?.PeriodCovered ?? null,
  };
}

function normalizeApprovalStatus(value: unknown) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "denied") return "rejected";
  if (["pending", "approved", "rejected"].includes(normalized)) return normalized;
  return "pending";
}

function buildStatusBadge(status: string, count: number, label: string) {
  return { status, count, label };
}

function reportCountLabel(count: number, plural: string, singular: string) {
  return count === 1 ? singular : plural;
}

function getInvoiceStatus(entry: any) {
  return entry?.isInvoiced ? "invoiced" : "not_invoiced";
}

function getInvoiceStatusLabel(status: string) {
  return status === "invoiced" ? "Payable" : "Not Invoiced";
}

function buildLandmanStatusReportData(apiData: any, approvalWorkflowEnabled: boolean) {
  const entries = Array.isArray(apiData?.entries) ? apiData.entries : [];
  const counts = {
    pending: 0,
    approved: 0,
    rejected: 0,
    invoiced: 0,
    notInvoiced: 0,
  };

  const nextEntries = entries.map((entry: any) => {
    const approvalStatusNormalized = normalizeApprovalStatus(entry.approvalStatus);
    const invoiceStatus = getInvoiceStatus(entry);

    if (approvalWorkflowEnabled) {
      counts[approvalStatusNormalized as "pending" | "approved" | "rejected"] += 1;
    }
    if (entry.isInvoiced) counts.invoiced += 1;
    else counts.notInvoiced += 1;

    return {
      ...entry,
      approvalStatusNormalized,
      invoiceStatus,
      rowStatus: approvalWorkflowEnabled && !entry.isInvoiced
        ? approvalStatusNormalized
        : invoiceStatus,
    };
  });

  const statusSummaryBadges = [
    buildStatusBadge("total", nextEntries.length, reportCountLabel(nextEntries.length, "entries", "entry")),
  ];

  if (approvalWorkflowEnabled) {
    if (counts.pending) statusSummaryBadges.push(buildStatusBadge("pending", counts.pending, "pending"));
    if (counts.approved) statusSummaryBadges.push(buildStatusBadge("approved", counts.approved, "approved"));
    if (counts.rejected) statusSummaryBadges.push(buildStatusBadge("rejected", counts.rejected, "rejected"));
    if (counts.invoiced) statusSummaryBadges.push(buildStatusBadge("invoiced", counts.invoiced, "invoiced"));
  } else {
    if (counts.invoiced) statusSummaryBadges.push(buildStatusBadge("invoiced", counts.invoiced, "invoiced"));
    if (counts.notInvoiced) statusSummaryBadges.push(buildStatusBadge("not_invoiced", counts.notInvoiced, "not invoiced"));
  }

  return {
    ...apiData,
    entries: nextEntries,
    statusSummaryBadges,
  };
}

function buildMyInvoiceReportData(apiData: any) {
  const timeDetails = Array.isArray(apiData?.timeDetails) ? apiData.timeDetails : [];
  let payableTotal = 0;
  let notInvoicedTotal = 0;
  let periodTotal = 0;
  let payableDays = 0;
  let notInvoicedDays = 0;
  let periodDays = 0;
  let invoicedCount = 0;
  let notInvoicedCount = 0;

  const nextTimeDetails = timeDetails.map((group: any) => {
    const entries = Array.isArray(group?.entries) ? group.entries : [];
    let groupPayableTotal = 0;
    let groupNotInvoicedTotal = 0;
    let groupPeriodTotal = 0;
    let groupPayableDays = 0;
    let groupNotInvoicedDays = 0;
    let groupPeriodDays = 0;
    let groupInvoicedCount = 0;
    let groupNotInvoicedCount = 0;

    const nextEntries = entries.map((entry: any) => {
      const total = Number(entry?.total || 0);
      const days = Number(entry?.days || 0);
      const invoiceStatus = getInvoiceStatus(entry);
      const isInvoiced = invoiceStatus === "invoiced";

      groupPeriodTotal += total;
      groupPeriodDays += days;
      if (isInvoiced) {
        groupPayableTotal += total;
        groupPayableDays += days;
        groupInvoicedCount += 1;
      } else {
        groupNotInvoicedTotal += total;
        groupNotInvoicedDays += days;
        groupNotInvoicedCount += 1;
      }

      return {
        ...entry,
        invoiceStatus,
        invoiceStatusLabel: getInvoiceStatusLabel(invoiceStatus),
        rowStatus: invoiceStatus,
      };
    });

    payableTotal += groupPayableTotal;
    notInvoicedTotal += groupNotInvoicedTotal;
    periodTotal += groupPeriodTotal;
    payableDays += groupPayableDays;
    notInvoicedDays += groupNotInvoicedDays;
    periodDays += groupPeriodDays;
    invoicedCount += groupInvoicedCount;
    notInvoicedCount += groupNotInvoicedCount;

    return {
      ...group,
      entries: nextEntries,
      payableTotal: groupPayableTotal,
      notInvoicedTotal: groupNotInvoicedTotal,
      periodTotal: groupPeriodTotal,
      payableDays: groupPayableDays,
      notInvoicedDays: groupNotInvoicedDays,
      periodDays: groupPeriodDays,
      invoiceStatusSummaryBadges: [
        buildStatusBadge("total", nextEntries.length, reportCountLabel(nextEntries.length, "entries", "entry")),
        ...(groupInvoicedCount ? [buildStatusBadge("invoiced", groupInvoicedCount, "payable")] : []),
        ...(groupNotInvoicedCount ? [buildStatusBadge("not_invoiced", groupNotInvoicedCount, "not invoiced")] : []),
      ],
    };
  });

  return {
    ...apiData,
    timeDetails: nextTimeDetails,
    payableTotal,
    notInvoicedTotal,
    periodTotal,
    payableDays,
    notInvoicedDays,
    periodDays,
    invoiceStatusSummaryBadges: [
      buildStatusBadge("total", invoicedCount + notInvoicedCount, reportCountLabel(invoicedCount + notInvoicedCount, "entries", "entry")),
      ...(invoicedCount ? [buildStatusBadge("invoiced", invoicedCount, "payable")] : []),
      ...(notInvoicedCount ? [buildStatusBadge("not_invoiced", notInvoicedCount, "not invoiced")] : []),
    ],
  };
}

function nextInvoiceCustomFieldDraftUid() {
  invoiceCustomFieldDraftUid += 1;
  return `invoice-custom-field-${invoiceCustomFieldDraftUid}`;
}

function getInvoiceSettingsSource(data: any): unknown {
  return data?.settings ?? data?.Settings;
}

function getInvoiceCustomFieldsSource(data: any): unknown {
  const settings = getInvoiceSettingsSource(data) as Record<string, unknown> | undefined;
  return settings?.customFields ?? settings?.CustomFields ?? data?.customFields ?? data?.CustomFields;
}

function getInvoiceReportOptionsSource(data: any): unknown {
  const settings = getInvoiceSettingsSource(data) as Record<string, unknown> | undefined;
  return (
    settings?.reportOptions ??
    settings?.ReportOptions ??
    data?.reportOptions ??
    data?.ReportOptions ??
    data?.datasets?.reportOptions ??
    data?.datasets?.ReportOptions
  );
}

function normalizeInvoiceReportOptions(value: unknown): Required<InvoiceReportOptions> {
  const source = value && typeof value === "object" ? value as Record<string, unknown> : {};
  return {
    splitSummariesByCounty: source.splitSummariesByCounty === true || source.SplitSummariesByCounty === true,
  };
}

function normalizeInvoiceCustomFields(fields: unknown): InvoiceCustomFieldDraft[] {
  if (!Array.isArray(fields)) return [];
  return fields
    .map((field: any, index) => {
      const rawPlacement = String(field?.placement ?? field?.Placement ?? "").toLowerCase();
      const placement: InvoiceCustomFieldPlacement = rawPlacement === "summary" ? "summary" : "header";
      const id = field?.id ?? field?.Id;
      const sortOrder = field?.sortOrder ?? field?.SortOrder;
      return {
        uid: String(field?.uid ?? field?.Uid ?? nextInvoiceCustomFieldDraftUid()),
        id: id == null ? undefined : Number(id) || undefined,
        label: String(field?.label ?? field?.Label ?? "").trim(),
        value: String(field?.value ?? field?.Value ?? ""),
        placement,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : index,
      };
    })
    .filter((field) => field.label)
    .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0));
}

function createBlankInvoiceCustomField(
  label = "",
  placement: InvoiceCustomFieldPlacement = "summary",
): InvoiceCustomFieldDraft {
  return { uid: nextInvoiceCustomFieldDraftUid(), label, value: "", placement };
}

function createSuggestedInvoiceCustomFieldDraft(
  field: Pick<InvoiceCustomFieldDraft, "label" | "value" | "placement">,
): InvoiceCustomFieldDraft {
  return {
    uid: nextInvoiceCustomFieldDraftUid(),
    label: field.label,
    value: field.value ?? "",
    placement: field.placement,
  };
}

function invoiceCustomFieldSuggestionKey(field: Pick<InvoiceCustomFieldDraft, "label" | "placement">) {
  return `${field.label.trim().toLowerCase()}::${field.placement}`;
}

function buildInvoiceCustomFieldPayload(fields: InvoiceCustomFieldDraft[]) {
  return fields
    .map((field, index) => ({
      ...(field.id ? { id: field.id } : {}),
      label: field.label.trim(),
      value: field.value ?? "",
      placement: field.placement,
      sortOrder: index,
    }))
    .filter((field) => field.label);
}

function buildInvoiceSettingsPayload(
  fields: InvoiceCustomFieldDraft[],
  reportOptions: InvoiceReportOptions,
): InvoiceReportSettings {
  return {
    customFields: buildInvoiceCustomFieldPayload(fields),
    reportOptions: normalizeInvoiceReportOptions(reportOptions),
  };
}

function validateInvoiceCustomFields(fields: InvoiceCustomFieldDraft[]) {
  const errors: string[] = [];
  const populated = fields.filter((field) => field.label.trim() || field.value.trim());

  if (populated.length > MAX_INVOICE_CUSTOM_FIELDS) {
    errors.push(`Custom fields are limited to ${MAX_INVOICE_CUSTOM_FIELDS} fields.`);
  }

  populated.forEach((field, index) => {
    const label = field.label.trim();
    const value = field.value ?? "";
    if (!label) errors.push(`Custom field ${index + 1} needs a label.`);
    if (label.length > MAX_INVOICE_CUSTOM_FIELD_LABEL_LENGTH) {
      errors.push(`Custom field "${label.slice(0, 24)}" label must be ${MAX_INVOICE_CUSTOM_FIELD_LABEL_LENGTH} characters or fewer.`);
    }
    if (value.length > MAX_INVOICE_CUSTOM_FIELD_VALUE_LENGTH) {
      errors.push(`Custom field "${label || index + 1}" value must be ${MAX_INVOICE_CUSTOM_FIELD_VALUE_LENGTH} characters or fewer.`);
    }
    if (!CUSTOM_FIELD_PLACEMENTS.some((item) => item.value === field.placement)) {
      errors.push(`Custom field "${label || index + 1}" has an invalid placement.`);
    }
  });

  return errors;
}

function groupInvoiceCustomFields(fields: InvoiceCustomFieldDraft[]) {
  return {
    header: fields.filter((field) => field.placement === "header"),
    summary: fields.filter((field) => field.placement === "summary"),
  };
}

function injectInvoiceCustomFields(template: any, data: any): { template: any; data: any } {
  const customFields = normalizeInvoiceCustomFields(getInvoiceCustomFieldsSource(data));
  const grouped = groupInvoiceCustomFields(customFields);
  const nextData: any = {
    ...data,
    invoiceCustomFields: customFields,
    invoiceCustomFieldsByPlacement: grouped,
  };

  if (!customFields.length) {
    return { template, data: nextData };
  }

  const nextTemplate = JSON.parse(JSON.stringify(template));
  const sections = nextTemplate?.layout?.sections || [];
  const headerFieldItem = (index: number) => ({
    label: nextData.invoiceCustomFieldsByPlacement.header[index].label,
    value: { path: `invoiceCustomFieldsByPlacement.header.${index}.value` },
  });
  const headerFieldsBlock = () => ({
    type: "group",
    className: "invoice-custom-header",
    blocks: [
      {
        type: "text",
        className: "custom-header-title",
        content: { expr: "'Additional Details'" },
      },
      {
        type: "keyValue",
        card: false,
        className: "kv-flat kv-chips invoice-custom-header-fields",
        items: grouped.header.map((_, index) => headerFieldItem(index)),
      },
    ],
  });

  const infoBand = sections.find((section: any) => section.id === "info-band");
  const injectHeaderItems = (blocks: any[]): boolean => {
    for (const block of blocks || []) {
      const nestedBlocks = Array.isArray(block.blocks) ? block.blocks : null;
      if (nestedBlocks) {
        const keyValueIndex = nestedBlocks.findIndex((nestedBlock: any) => nestedBlock.type === "keyValue" && Array.isArray(nestedBlock.items));
        if (keyValueIndex >= 0) {
          nestedBlocks.splice(keyValueIndex + 1, 0, headerFieldsBlock());
          return true;
        }
        if (injectHeaderItems(nestedBlocks)) return true;
      }
      if (block.type === "keyValue" && Array.isArray(block.items)) {
        blocks.splice(blocks.indexOf(block) + 1, 0, headerFieldsBlock());
        return true;
      }
    }
    return false;
  };
  if (grouped.header.length && infoBand?.blocks) {
    injectHeaderItems(infoBand.blocks);
  }

  const summarySection = sections.find((section: any) => section.id === "summary-section");
  if (grouped.summary.length && Array.isArray(summarySection?.blocks)) {
    const summaryTable = summarySection.blocks.find((block: any) => block.type === "table" && block.rows?.path === "summaryRows");
    const columns = Array.isArray(summaryTable?.columns) ? summaryTable.columns : null;
    if (columns) {
      const summaryFieldColumns = grouped.summary.map((field, index) => ({
        key: `invoiceCustomField_summary_${index}`,
        label: field.label,
        width: 110,
      }));
      const totalIndex = columns.findIndex((column: any) => column.key === "total");
      columns.splice(totalIndex >= 0 ? totalIndex : columns.length, 0, ...summaryFieldColumns);
      const summaryRows = Array.isArray(nextData.summaryRows) ? nextData.summaryRows : [];
      nextData.summaryRows = summaryRows.map((row: any) => {
        const nextRow = { ...row };
        grouped.summary.forEach((field, index) => {
          nextRow[`invoiceCustomField_summary_${index}`] = field.value;
        });
        return nextRow;
      });
    }
  }

  return { template: nextTemplate, data: nextData };
}

/** Strip template sections that require a feature the tenant doesn't have. */
function filterTemplateSections(
  template: any,
  activeFeatures: Set<string>,
  columnFilter?: (col: { key: string }) => boolean,
) {
  const sections = template?.layout?.sections;
  if (!sections) return template;
  let filtered = sections.filter(
    (sec: any) => !sec.feature || activeFeatures.has(sec.feature),
  );
  if (columnFilter) {
    filtered = filtered.map((sec: any) => ({
      ...sec,
      blocks: (sec.blocks || []).map((block: any) =>
        block.type === "table" && block.columns
          ? { ...block, columns: block.columns.filter(columnFilter) }
          : block,
      ),
    }));
  }
  return {
    ...template,
    layout: {
      ...template.layout,
      sections: filtered,
    },
  };
}

/**
 * For any report with expenseColumns in the data payload, flatten expense
 * amounts onto each row and inject dynamic columns into template tables
 * that have dynamicExpenses: true.
 */
function injectDynamicExpenses(template: any, data: any): { template: any; data: any } {
  const sections = template?.layout?.sections;
  const expenseColumns: any[] = data?.expenseColumns ?? [];
  if (!sections || expenseColumns.length === 0) return { template, data };

  // Find all table blocks and their row paths
  const dynamicPaths: string[] = [];
  const allTablePaths: string[] = [];
  const walkBlocks = (blocks: any[]) => {
    for (const block of blocks) {
      if (block.type === "table" && block.rows?.path) {
        allTablePaths.push(block.rows.path);
        if (block.dynamicExpenses) dynamicPaths.push(block.rows.path);
      }
      if (block.type === "repeat" && block.blocks) walkBlocks(block.blocks);
      if (block.type === "group" && block.blocks) walkBlocks(block.blocks);
    }
  };
  for (const sec of sections) {
    if (sec.blocks) walkBlocks(sec.blocks);
  }

  // Build dynamic columns from expenseColumns metadata
  // Integer types (e.g., Mileage): show qty + amount only when client.mileageRate exists,
  // otherwise just qty. Other types always show amount.
  const mileageRate = data?.client?.mileageRate;
  const expenseCols = expenseColumns.flatMap((ec: any) => {
    if (ec.inputType === "integer") {
      const cols: { key: string; label: string; format?: string; width?: number }[] = [
        { key: `expense_${ec.name}_qty`, label: ec.name, width: 80 },
      ];
      if (mileageRate) {
        cols.push({ key: `expense_${ec.name}`, label: `${ec.name} @$${mileageRate}/mi`, format: "money", width: 120 });
      }
      return cols;
    }
    return [{ key: `expense_${ec.name}`, label: ec.name, format: "money", width: 110 }];
  });

  // Inject columns into dynamicExpenses templates (before "total" column)
  const newTemplate = dynamicPaths.length > 0 ? JSON.parse(JSON.stringify(template)) : template;
  if (dynamicPaths.length > 0) {
    const injectCols = (blocks: any[]) => {
      for (const block of blocks) {
        if (block.type === "table" && block.dynamicExpenses && block.columns) {
          const idx = block.columns.findIndex((c: any) => c.key === "total");
          if (idx >= 0) block.columns.splice(idx, 0, ...expenseCols);
          else block.columns.push(...expenseCols);

          // Inject matching footer cells to align with expense columns
          if (block.footer?.cells) {
            const rowsPath = block.rows?.path || "entries";
            const footerCells = expenseCols.map((col: any) => ({
              expr: col.format === "money"
                ? `money(sum(${rowsPath}.map(e=>e['${col.key}']||0)))`
                : `sum(${rowsPath}.map(e=>e['${col.key}']||0))`,
            }));
            if (idx >= 0) block.footer.cells.splice(idx, 0, ...footerCells);
            else block.footer.cells.push(...footerCells);
          }
        }
        if (block.type === "repeat" && block.blocks) injectCols(block.blocks);
        if (block.type === "group" && block.blocks) injectCols(block.blocks);
      }
    };
    for (const sec of newTemplate.layout.sections) {
      if (sec.blocks) injectCols(sec.blocks);
    }
  }

  // Flatten expenses onto each data row (all tables, not just dynamic)
  const newData = JSON.parse(JSON.stringify(data));
  const flattenRows = (rows: any[]) => {
    for (const row of rows) {
      if (row.expenses && typeof row.expenses === "object" && !Array.isArray(row.expenses)) {
        let total = 0;
        for (const ec of expenseColumns) {
          const key = String(ec.id ?? ec.name);
          const exp = row.expenses[key];
          const amt = Number(exp?.amount ?? 0);
          row[`expense_${ec.name}`] = amt;
          row[`expense_${ec.name}_qty`] = exp?.quantity ?? 0;
          total += amt;
        }
        row.totalExpenses = total;
      }
    }
  };

  // Flatten in top-level arrays
  const allPaths = Array.from(new Set([...allTablePaths, "entries"]));
  for (const path of allPaths) {
    if (Array.isArray(newData[path])) flattenRows(newData[path]);
  }
  // Flatten in nested arrays (e.g., brokers[].entries)
  for (const val of Object.values(newData)) {
    if (Array.isArray(val)) {
      for (const item of val as any[]) {
        if (item && typeof item === "object") {
          for (const path of allPaths) {
            if (Array.isArray(item[path])) flattenRows(item[path]);
          }
        }
      }
    }
  }

  return { template: newTemplate, data: newData };
}

function CalendarMiniIcon() {
  return (
    <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect width="28" height="28" rx="6" fill="#397FFB" />
      <path d="M11.3335 7.3335V9.3335" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.6665 7.3335V9.3335" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.3335 12.0603H19.6668" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 11.6668V17.3335C20 19.3335 19 20.6668 16.6667 20.6668H11.3333C9 20.6668 8 19.3335 8 17.3335V11.6668C8 9.66683 9 8.3335 11.3333 8.3335H16.6667C19 8.3335 20 9.66683 20 11.6668Z" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.463 15.1331H16.469" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.463 17.1331H16.469" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.9972 15.1331H14.0031" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.9972 17.1331H14.0031" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.5294 15.1331H11.5354" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.5294 17.1331H11.5354" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M10 3h4l.5 2.5 1.5.9 2.5-.8 2 3.4-2 1.8v1.8l2 1.8-2 3.4-2.5-.8-1.5.9-.5 2.5h-4l-.5-2.5-1.5-.9-2.5.8-2-3.4 2-1.8v-1.8l-2-1.8 2-3.4 2.5.8 1.5-.9L10 3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function PrintIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.833 7.083V3.75h8.334v3.333M5.417 12.917H4.583A1.667 1.667 0 0 1 2.917 11.25V8.75A1.667 1.667 0 0 1 4.583 7.083h10.834A1.667 1.667 0 0 1 17.083 8.75v2.5a1.667 1.667 0 0 1-1.666 1.667h-.834M5.833 10.833h8.334v5.417H5.833v-5.417Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="14.792" cy="9.792" r="0.625" fill="currentColor" />
    </svg>
  );
}

function ReceiptIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 2.917h10v13.75l-1.667-1.25-1.666 1.25L10 15.417l-1.667 1.25-1.666-1.25L5 16.667V2.917Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 7.083h5M7.5 9.583h5M7.5 12.083h3.333"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 15V5M5.833 9.167 10 5l4.167 4.167"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 5v10M5.833 10.833 10 15l4.167-4.167"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExcelIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="3" y="2" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function formatForApi(d: any) {
  if (!d) return null;
  const date = toJsDate(d);
  if (!date) return null;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseApiDateAsLocal(value: unknown) {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

const getPresets = (rptId: string) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  const thisMonthStart = new Date(year, month, 1);
  const thisMonthEnd = new Date(year, month + 1, 0);
  const lastMonthStart = new Date(year, month - 1, 1);
  const lastMonthEnd = new Date(year, month, 0);

  const isInvoiceReport = ["invoice", "outstanding", "myInvoice", "landman", "landmanPaymentSummary"].includes(rptId);

  if (isInvoiceReport) {
    const currentHalfStart = day <= 15 ? new Date(year, month, 1) : new Date(year, month, 16);
    const currentHalfEnd = day <= 15 ? new Date(year, month, 15) : thisMonthEnd;
    const prevHalfStart = day <= 15 ? new Date(year, month - 1, 16) : new Date(year, month, 1);
    const prevHalfEnd = day <= 15 ? lastMonthEnd : new Date(year, month, 15);

    return [
      { label: "Current Half", start: currentHalfStart, end: currentHalfEnd },
      { label: "Previous Half", start: prevHalfStart, end: prevHalfEnd },
      { label: "This Month", start: thisMonthStart, end: thisMonthEnd },
      { label: "Last Month", start: lastMonthStart, end: lastMonthEnd },
    ];
  }

  // Time entry reports
  const today = new Date(year, month, day);
  const dayOfWeek = today.getDay();
  const lastSunday = new Date(today); lastSunday.setDate(today.getDate() - dayOfWeek);
  const lastMonday = new Date(lastSunday); lastMonday.setDate(lastSunday.getDate() - 6);
  const twoWeeksAgo = new Date(today); twoWeeksAgo.setDate(today.getDate() - 14);

  return [
    { label: "Last Week", start: lastMonday, end: lastSunday },
    { label: "Last 2 Weeks", start: twoWeeksAgo, end: today },
    { label: "This Month", start: thisMonthStart, end: thisMonthEnd },
    { label: "Last Month", start: lastMonthStart, end: lastMonthEnd },
  ];
};

function toJsDate(value: any) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDisplayDate(value: any) {
  const d = toJsDate(value);
  return d ? d.toLocaleDateString() : "";
}

function formatPeriodLabel(startValue: any, endValue: any) {
  const start = toJsDate(startValue);
  const end = toJsDate(endValue);
  if (!start || !end) return "";
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
}

function normalizeBillingPeriodMode(value: unknown): "unset" | "monthly" | "semi_monthly" {
  const normalized = String(value || "unset").trim().toLowerCase();
  return normalized === "monthly" || normalized === "semi_monthly" ? normalized : "unset";
}

function getDefaultInvoiceRange() {
  const now = new Date();
  const isFirstHalf = now.getDate() <= 15;

  if (isFirstHalf) {
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const from = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 16);
    const to = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
    return { from, to };
  }

  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 15);
  return { from, to };
}

async function startPdfExport({
  template, data, options, authToken, tenantId, userId, getToken, isCurrent, meta,
}: {
  template: any; data: any; options?: any;
  meta: Record<string, unknown>;
  authToken: string; tenantId: string; userId: number;
  getToken: () => Promise<string>;
  isCurrent?: () => boolean;
}) {
  const api = createApiClient({ baseUrl: getPdfServerUrl(), token: authToken, getToken, tenantId, userId });
  const result = await api.post<unknown>("reports/pdf-token", { template, data, options, ...buildRenderContext(meta, "pdf", "v2") });
  if (isCurrent && !isCurrent()) return;
  if (!result || typeof result !== "object" || !("token" in result) || typeof result.token !== "string" || !result.token.trim()) {
    throw new Error("The PDF server did not return a valid export token.");
  }
  const pdfToken = result.token;
  const blob = await api.getBlob(`reports/pdf?token=${encodeURIComponent(pdfToken)}`, { accept: "application/pdf" });
  if (isCurrent && !isCurrent()) return;
  if (!blob.type.toLowerCase().includes("application/pdf")) throw new Error("The PDF server did not return a PDF file.");
  if (blob.size > 10 * 1024 * 1024) throw new Error("The PDF exceeds the supported output size.");
  const signature = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("The PDF file could not be read."));
    reader.readAsText(blob.slice(0, 5));
  });
  if (isCurrent && !isCurrent()) return;
  if (signature !== "%PDF-") throw new Error("The PDF server did not return a complete PDF file.");
  const objectUrl = window.URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

async function exportReportsV3Workbook({
  payload,
  fileName,
  authToken,
  tenantId,
  userId,
  getToken,
  isCurrent,
  meta,
}: {
  payload: FormattedInvoicePayload;
  meta: Record<string, unknown>;
  fileName: string;
  authToken: string;
  tenantId: string;
  userId: number;
  getToken?: () => Promise<string>;
  isCurrent?: () => boolean;
}) {
  assertReportAdmission(payload);
  if (payload.schemaVersion === "reports-v3.1") {
    return exportNativeExcel({ payload, fileName, token: authToken, getToken, tenantId, userId, sourceMode: "business", isCurrent, renderContext: buildRenderContext(meta, "excel", "v3") });
  }
  const api = createApiClient({ baseUrl: getPdfServerUrl(), token: authToken, getToken, tenantId, userId });
  const response = await api.postResponse("reports/reportsv3/excel", { payload, fileName, ...buildRenderContext(meta, "excel", "v3") }, {
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const blob = await readValidatedReportsV3ExportBlob(response, "excel");
  if (isCurrent && !isCurrent()) return;
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName.toLowerCase().endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

async function exportReportsV3Pdf({
  payload,
  fileName,
  authToken,
  tenantId,
  userId,
  getToken,
  isCurrent,
  meta,
}: {
  payload: FormattedInvoicePayload;
  meta: Record<string, unknown>;
  fileName: string;
  authToken: string;
  tenantId: string;
  userId: number;
  getToken: () => Promise<string>;
  isCurrent?: () => boolean;
}) {
  assertReportAdmission(payload);
  const api = createApiClient({ baseUrl: getPdfServerUrl(), token: authToken, getToken, tenantId, userId });
  const response = await api.postResponse("reports/reportsv3/pdf", { payload, fileName, ...buildRenderContext(meta, "pdf", "v3") }, { accept: "application/pdf" });

  const blob = await readValidatedReportsV3ExportBlob(response, "pdf");
  if (isCurrent && !isCurrent()) return;
  const objectUrl = window.URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

async function readValidatedReportsV3ExportBlob(response: Response, kind: "pdf" | "excel"): Promise<Blob> {
  const bytes = new Uint8Array(await response.arrayBuffer());
  const contentType = response.headers.get("content-type") || "";
  const expected = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const validSignature = kind === "pdf"
    ? bytes.length >= 5 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d
    : bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;
  if (!contentType.toLowerCase().includes(expected) || !validSignature) {
    const preview = decodeExportResponsePreview(bytes);
    const responseKind = contentType.includes("text/html") || preview.trimStart().startsWith("<!DOCTYPE html") || preview.trimStart().startsWith("<html")
      ? "HTML page"
      : contentType.includes("application/json")
        ? "JSON response"
        : contentType || "unexpected response";
    throw new Error(`Expected a ${kind.toUpperCase()} file but received ${responseKind}. Verify the ReportsV3 export route and authentication headers.`);
  }
  return new Blob([bytes], { type: expected });
}

function decodeExportResponsePreview(bytes: Uint8Array): string {
  if (!bytes.length) return "";
  try {
    return new TextDecoder().decode(bytes.slice(0, 300)).replace(/\s+/g, " ").trim();
  } catch {
    return "";
  }
}

const ReportsInner = () => {
  const mounted = useRef(true);
  const accessEpoch = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const captureAccess = useCallback(() => {
    const epoch = accessEpoch.current;
    return () => mounted.current && accessEpoch.current === epoch;
  }, []);
  const defaultRange = useMemo(() => getDefaultInvoiceRange(), []);
  const { token, getToken, error: tokenError } = useAuthToken();
  const { user, loading, activeTenantId } = useUser();
  const { hasFeature, isVisible, tier, loading: tierLoading } = useTier();
  const ready = !!token && !!user && !loading && !tierLoading;

  const roleId = Number(user?.roleId);
  const canUseTimeEntryDetails = roleId === Roles.Owner || roleId === Roles.Admin;
  const [reportId, setReportId] = useState(canUseTimeEntryDetails ? "timeEntryDetails" : "myInvoice");

  const canExportExcel = hasFeature("raw_excel_export");
  const canPrintPdf = hasFeature("client_ready_reports");
  const approvalWorkflowEnabled = hasFeature("approval_workflow");

  const rShowClient = isVisible("reports_show_client");
  const rShowProspect = isVisible("reports_show_prospect");
  const rShowLegal = isVisible("reports_show_legal");
  const rShowJob = isVisible("reports_show_job");
  const rShowDescription = isVisible("reports_show_description");

  const visibilityColumnFilter = useCallback((col: { key: string }) => {
    const keyMap: Record<string, boolean> = {
      client: rShowClient,
      prospect: rShowProspect,
      legalDescription: rShowLegal,
      legal: rShowLegal,
      jobType: rShowJob,
      description: rShowDescription,
      approverName: approvalWorkflowEnabled,
    };
    return keyMap[col.key] ?? true;
  }, [approvalWorkflowEnabled, rShowClient, rShowProspect, rShowLegal, rShowJob, rShowDescription]);

  const activeFeatures = useMemo(() => {
    const s = new Set<string>();
    if (hasFeature("client_ready_reports")) s.add("client_ready_reports");
    if (hasFeature("invoice_generation")) s.add("invoice_generation");
    if (approvalWorkflowEnabled) s.add("approval_workflow");
    return s;
  }, [approvalWorkflowEnabled, hasFeature]);

  const prepareReportOutput = useCallback(
    (template: any, data: any) => {
      const filteredTemplate = filterTemplateSections(template, activeFeatures, visibilityColumnFilter);
      const withCustomFields = injectInvoiceCustomFields(filteredTemplate, data);
      return injectDynamicExpenses(withCustomFields.template, withCustomFields.data);
    },
    [activeFeatures, visibilityColumnFilter],
  );

  const [reportsV3Definitions, setReportsV3Definitions] = useState<ReportDefinitionSummaryV3[]>([]);
  const [reportsV3DefinitionsLoaded, setReportsV3DefinitionsLoaded] = useState(false);
  const [catalogFailure, setCatalogFailure] = useState<{ message: string; denied: boolean } | null>(null);
  const [catalogReload, setCatalogReload] = useState(0);
  const reportsReady = ready && reportsV3DefinitionsLoaded;

  const visibleReports = useMemo<any[]>(() => {
    if (!reportsReady || !roleId || catalogFailure?.denied) return [];
    const reportsV3 = reportsV3Definitions
      .map(buildReportsV3ReportDefinition)
      .filter(Boolean);
    const resolvedReportsV3 = resolveReportsV3Definitions(reportsV3);
    const reportsV3Keys = new Set(resolvedReportsV3.map(getReportKey));
    const legacyReports = REPORTS
      .filter((report) => !reportsV3Keys.has(report.id))
      .map((report) => ({ ...report, reportKey: report.id, engine: "v2" }));
    return [...legacyReports, ...resolvedReportsV3].filter((r: any) => {
      if (getReportKey(r) === "timeEntryDetails" && !canUseTimeEntryDetails) return false;
      if (!r.roles || !r.roles.includes(roleId)) return false;
      if (r.freeCrewOnly && tier !== "free_crew") return false;
      if (r.feature && !hasFeature(r.feature)) return false;
      if (r.customFeature && !hasFeature(r.customFeature)) return false;
      return true;
    });
  }, [reportsReady, roleId, canUseTimeEntryDetails, tier, hasFeature, reportsV3Definitions, catalogFailure]);

  useEffect(() => {
    if (!reportsReady || !visibleReports.length) return;
    const allowed = visibleReports.some((r: any) => r.id === reportId);
    if (allowed) return;
    const replacement = visibleReports.find((r: any) => getReportKey(r) === reportId);
    setReportId((replacement || visibleReports[0]).id);
  }, [reportsReady, visibleReports, reportId]);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  useEffect(() => {
    if (!ready || !api) {
      setReportsV3Definitions([]);
      setReportsV3DefinitionsLoaded(false);
      return;
    }

    let cancelled = false;
    async function loadReportsV3Definitions() {
      setReportsV3DefinitionsLoaded(false);
      setReportsV3Definitions([]);
      setCatalogFailure(null);
      try {
        const definitions = await api!.get<ReportDefinitionSummaryV3[]>("ReportsV3/definitions");
        if (!Array.isArray(definitions)) throw new Error("The report catalog response was invalid.");
        if (!cancelled) {
          setReportsV3Definitions(definitions);
          setReportsV3DefinitionsLoaded(true);
        }
      } catch (err) {
        console.error("Failed to load ReportsV3 definitions:", reportFailureMessage(err, "Report catalog unavailable."));
        if (!cancelled) {
          setReportsV3Definitions([]);
          if (isReportAccessDenied(err)) accessEpoch.current += 1;
          setCatalogFailure({
            denied: isReportAccessDenied(err),
            message: isReportAccessDenied(err)
              ? reportFailureMessage(err, "Report access is unavailable.")
              : "Saved reports are unavailable. Showing legacy reports only. Refresh access to try again.",
          });
          setReportsV3DefinitionsLoaded(true);
        }
      }
    }

    void loadReportsV3Definitions();
    return () => {
      cancelled = true;
    };
  }, [ready, api, hasFeature, catalogReload]);

  const reportDef = useMemo<any>(() => {
    if (!visibleReports.length) return null;
    return visibleReports.find((r: any) => r.id === reportId) || visibleReports[0];
  }, [reportId, visibleReports]);
  const reportKey = getReportKey(reportDef);
  const reportHelpUrl = getSystemReportHelpUrl(reportKey);
  const supportsIncludeSetting = isReportsV3Report(reportDef) && LANDMAN_INCLUDE_REPORT_KEYS.has(reportKey);
  const settingsContext = JSON.stringify([activeTenantId, user?.userId, reportId]);
  const [runSettings, setRunSettings] = useState<{ context: string; invoicedOnly: boolean } | null>(null);
  const invoicedOnly = runSettings?.context === settingsContext ? runSettings.invoicedOnly : true;
  const [draftInvoicedOnly, setDraftInvoicedOnly] = useState(true);

  const [clients, setClients] = useState<any[]>([]);
  const [clientId, setClientId] = useState("");
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriodResponse | null>(null);

  const [startDate, setStartDate] = useState(defaultRange.from);
  const [endDate, setEndDate] = useState(defaultRange.to);
  const [billingPeriodAnchorDate, setBillingPeriodAnchorDate] = useState(defaultRange.from);
  const [invoiceSearchRangeActive, setInvoiceSearchRangeActive] = useState(false);
  const startDateRef = useRef(defaultRange.from);

  const [existingDocs, setExistingDocs] = useState<any[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [hasRecentInvoiceHistory, setHasRecentInvoiceHistory] = useState(false);
  const [invoiceRangeSearchMissed, setInvoiceRangeSearchMissed] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(0);
  const [selectedDocName, setSelectedDocName] = useState("N/A");
  const [loadingDocId, setLoadingDocId] = useState(0);

  const [receiptSummary, setReceiptSummary] = useState<any>(null);
  const [loadingReceiptSummary, setLoadingReceiptSummary] = useState(false);
  const [isDownloadingReceipts, setIsDownloadingReceipts] = useState(false);
  const [showUninvoicedTimeWarning, setShowUninvoicedTimeWarning] = useState(false);
  const uninvoicedTimeCheckSeqRef = useRef(0);
  const invoiceListLoadSeqRef = useRef(0);

  const [suggestedInvoiceName, setSuggestedInvoiceName] = useState("");
  const [showInvoiceNameModal, setShowInvoiceNameModal] = useState(false);
  const [invoiceNameDraft, setInvoiceNameDraft] = useState("");
  const [invoiceCustomFieldDrafts, setInvoiceCustomFieldDrafts] = useState<InvoiceCustomFieldDraft[]>([]);
  const [suggestedInvoiceCustomFields, setSuggestedInvoiceCustomFields] = useState<InvoiceCustomFieldDraft[]>([]);
  const [invoiceReportOptions, setInvoiceReportOptions] = useState<InvoiceReportOptions>(DEFAULT_INVOICE_REPORT_OPTIONS);
  const [customFieldModalMode, setCustomFieldModalMode] = useState<"generate" | "edit" | "run">("generate");
  const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(false);
  const [isSavingCustomFields, setIsSavingCustomFields] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  const [pendingDeleteDoc, setPendingDeleteDoc] = useState<any>(null);
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);

  const [isRunning, setIsRunning] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [reportError, setReportError] = useState("");
  const [result, setResult] = useState<any>(null);
  const reportRunSequence = useRef(0);
  const executionContext = JSON.stringify([settingsContext, reportDef?.requiresClient ? clientId : null]);
  const executionContextRef = useRef(executionContext);
  executionContextRef.current = executionContext;
  useEffect(() => {
    reportRunSequence.current += 1;
    setIsRunning(false);
  }, [executionContext]);
  useEffect(() => {
    setRunSettings(null);
    setShowInvoiceNameModal(false);
  }, [settingsContext]);
  const canAccessResult = result?.meta?.reportId !== "timeEntryDetails" || canUseTimeEntryDetails;

  const [activeSort, setActiveSort] = useState<{ key: string | null; dir: 'asc' | 'desc'; rowsPath?: string } | null>(null);

  const clearDisplayedReportState = useCallback(() => {
    reportRunSequence.current += 1;
    setIsRunning(false);
    setResult(null);
    setReceiptSummary(null);
    setLoadingReceiptSummary(false);
    setReportError("");
    setActiveSort(null);
    setPendingDeleteDoc(null);
    setShowInvoiceNameModal(false);
    setSelectedDocId(0);
    setSelectedDocName("N/A");
    setLoadingDocId(0);
    setShowUninvoicedTimeWarning(false);
    uninvoicedTimeCheckSeqRef.current += 1;
  }, []);
  const reportOperationFailure = useCallback((error: unknown, fallback: string) => {
    const message = reportFailureMessage(error, reportErrorMessage(error, fallback));
    if (isReportAccessDenied(error)) {
      accessEpoch.current += 1;
      clearDisplayedReportState();
      invoiceListLoadSeqRef.current += 1;
      setIsLoadingInvoices(false);
      setClients([]);
      setClientId("");
      setExistingDocs([]);
      setBillingPeriod(null);
      setSuggestedInvoiceName("");
      setSuggestedInvoiceCustomFields([]);
      setReportsV3Definitions([]);
      setCatalogFailure({ denied: true, message });
    }
    return message;
  }, [clearDisplayedReportState]);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const loadClients = useCallback(async () => {
    const isCurrent = captureAccess();
    try {
      if (!api) return null;

      const data = await api.get("clientinfo/lookup");
      if (!isCurrent()) return null;
      const list = Array.isArray(data) ? data : [];

      list.sort((a: any, b: any) =>
        (a.businessName || "")
          .toLowerCase()
          .localeCompare((b.businessName || "").toLowerCase()),
      );

      setClients(list);
      setClientId((prev: any) =>
        prev ? prev : list?.length ? String(list[0].clientId) : "",
      );

      return list;
    } catch (err: any) {
      if (!isCurrent()) return null;
      console.error("❌ Error loading clients:", reportFailureMessage(err, "Client lookup failed."));
      setToast({ message: reportOperationFailure(err, "Failed to load authorized clients."), type: "error" });
      return null;
    }
  }, [api, captureAccess, reportOperationFailure]);

  const loadInvoicesForSelection = useCallback(async (
    nextClientId: any = clientId,
    nextStartDate: any = startDate,
    nextEndDate: any = endDate,
    options: { forceRecent?: boolean } = {},
  ) => {
    const loadSeq = ++invoiceListLoadSeqRef.current;
    const isCurrent = captureAccess();
    const isCurrentLoad = () => isCurrent() && loadSeq === invoiceListLoadSeqRef.current;
    setIsLoadingInvoices(true);
    try {
      if (!api || !nextClientId) {
        if (isCurrentLoad()) setExistingDocs([]);
        return [];
      }

      const start = formatForApi(nextStartDate);
      const end = formatForApi(nextEndDate);
      const shouldUseDateRange = !options.forceRecent && start && end;
      const data = shouldUseDateRange
        ? await api.get(`ReportsV2/GetInvoices/${nextClientId}/${start}/${end}`)
        : await api.get(`ReportsV2/GetRecentInvoices/${nextClientId}/${RECENT_INVOICE_TAKE}`);
      const list = Array.isArray(data) ? data.map(normalizeInvoiceDoc) : [];

      if (shouldUseDateRange && list.length === 0) {
        const recentData = await api.get(`ReportsV2/GetRecentInvoices/${nextClientId}/${RECENT_INVOICE_TAKE}`);
        const recentList = Array.isArray(recentData) ? recentData.map(normalizeInvoiceDoc) : [];
        if (!isCurrentLoad()) return [];
        setHasRecentInvoiceHistory(recentList.length > 0);
        if (reportKey === "invoice") {
          setInvoiceRangeSearchMissed(false);
          setExistingDocs([]);
          return [];
        }
        setInvoiceRangeSearchMissed(true);
        setExistingDocs(recentList);
        return recentList;
      }

      if (isCurrentLoad()) {
        setInvoiceRangeSearchMissed(false);
        if (!shouldUseDateRange) {
          setHasRecentInvoiceHistory(list.length > 0);
        }
        setExistingDocs(list);
      }
      return list;
    } catch (err: any) {
      console.error("❌ Error loading invoices:", reportFailureMessage(err, "Invoice lookup failed."));
      if (isCurrentLoad()) {
        setExistingDocs([]);
        setHasRecentInvoiceHistory(false);
        setInvoiceRangeSearchMissed(false);
        setToast({ message: reportOperationFailure(err, "Failed to load invoice history."), type: "error" });
      }
      return [];
    } finally {
      if (isCurrentLoad()) setIsLoadingInvoices(false);
    }
  }, [api, captureAccess, clientId, endDate, reportKey, startDate, reportOperationFailure]);

  const loadSuggestedInvoiceName = useCallback(async (nextClientId: any = clientId) => {
    const isCurrent = captureAccess();
    try {
      if (!api || !nextClientId || reportKey !== "invoice") {
        setSuggestedInvoiceName("");
        return "";
      }

      const data = await api.get(
        `ReportsV2/GetSuggestedInvoiceName/${nextClientId}`,
      );
      if (!isCurrent()) return "";
      const name = String((data as any)?.invoiceName || "").trim();
      setSuggestedInvoiceName(name);
      return name;
    } catch (err: any) {
      if (!isCurrent()) return "";
      console.error("❌ Error loading suggested invoice name:", reportFailureMessage(err, "Invoice name lookup failed."));
      setSuggestedInvoiceName("");
      if (isReportAccessDenied(err)) setToast({ message: reportOperationFailure(err, "Failed to load invoice name suggestions."), type: "error" });
      return "";
    }
  }, [api, captureAccess, clientId, reportKey, reportOperationFailure]);

  const loadSuggestedInvoiceCustomFields = useCallback(async (nextClientId: any = clientId) => {
    const isCurrent = captureAccess();
    try {
      if (!api || !nextClientId || reportKey !== "invoice") {
        setSuggestedInvoiceCustomFields([]);
        return [];
      }

      const data = await api.get<SuggestedInvoiceCustomField[]>(
        `ReportsV2/GetSuggestedInvoiceCustomFields/${nextClientId}`,
      );
      if (!isCurrent()) return [];
      const fields = normalizeInvoiceCustomFields(data);
      setSuggestedInvoiceCustomFields(fields);
      return fields;
    } catch (err: any) {
      if (!isCurrent()) return [];
      console.error("❌ Error loading suggested invoice custom fields:", reportFailureMessage(err, "Invoice field lookup failed."));
      setSuggestedInvoiceCustomFields([]);
      if (isReportAccessDenied(err)) setToast({ message: reportOperationFailure(err, "Failed to load invoice field suggestions."), type: "error" });
      return [];
    }
  }, [api, captureAccess, clientId, reportKey, reportOperationFailure]);

  const loadBillingPeriod = useCallback(async (
    nextClientId: any = clientId,
    asOfDate: any = billingPeriodAnchorDate,
  ) => {
    const isCurrent = captureAccess();
    if (!api || !nextClientId || !reportDef?.requiresClient || !reportDef?.requiresDateRange) {
      setBillingPeriod(null);
      return null;
    }

    try {
      const asOf = formatForApi(asOfDate);
      const data = await api.get<BillingPeriodResponse>(
        `ReportsV2/BillingPeriod/${nextClientId}`,
        asOf ? { query: { asOf } } : undefined,
      );
      if (!isCurrent()) return null;
      setBillingPeriod(data && typeof data === "object" ? data : null);
      return data;
    } catch (err) {
      if (!isCurrent()) return null;
      console.error("❌ Error loading billing period:", reportFailureMessage(err, "Billing period lookup failed."));
      setBillingPeriod(null);
      if (isReportAccessDenied(err)) setToast({ message: reportOperationFailure(err, "Failed to load the billing period."), type: "error" });
      return null;
    }
  }, [api, billingPeriodAnchorDate, captureAccess, clientId, reportDef, reportOperationFailure]);

  const downloadBlob = async (path: any, fileName: any) => {
    const isCurrent = captureAccess();
    if (!api) throw new Error("API client is not ready.");

    const blob = await api.getBlob(path, { accept: "application/zip" });
    if (!isCurrent()) throw new DOMException("Report access changed.", "AbortError");
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const validate = (
    effectiveClientId: any = clientId,
    effectiveStartDate: any = startDate,
    effectiveEndDate: any = endDate,
  ) => {
    const errs = [];

    if (reportDef?.requiresClient && !effectiveClientId) {
      errs.push("Please select a client.");
    }

    if (reportDef?.requiresDateRange) {
      if (!effectiveStartDate) errs.push("Please select a start date.");
      if (!effectiveEndDate) errs.push("Please select an end date.");
      if (
        effectiveStartDate &&
        effectiveEndDate &&
        effectiveStartDate > effectiveEndDate
      ) {
        errs.push("Start date must be before end date.");
      }
    }

    return errs;
  };

  const fetchReportPayloadV2 = async ({
    reportId,
    clientId,
    startDate,
    endDate,
    invoiceId,
    outstandingId,
  }: any) => {
    if (!api) throw new Error("Missing auth token");

    let path = "";

    switch (reportId) {
      case "invoice":
        path = Number(invoiceId) > 0
          ? `ReportsV2/Invoice/${invoiceId}`
          : `ReportsV2/InvoicePreview/${clientId}/${startDate}/${endDate}`;
        break;
      case "outstanding":
        path = `ReportsV2/Outstanding/${outstandingId}`;
        break;
      case "myInvoice":
        path = `ReportsV2/MyInvoice/${startDate}/${endDate}`;
        break;
      case "landman":
      case "landmanPaymentSummary":
        path = `ReportsV2/Landman/${startDate}/${endDate}`;
        break;
      case "landmanStatus":
      case "timeEntryDetails":
        path = `ReportsV2/TimeDetails/${clientId}/${startDate}/${endDate}`;
        break;
      default:
        throw new Error(`Unknown report id: ${reportId}`);
    }

    return await api.get(path);
  };

  // Transform Landman Status Report data: flatten expenses, resolve approver names
  // Resolve approver names for Time Details / Landman Status reports
  const resolveApproverNames = (apiData: any, userMap: Record<number, string>) => {
    const { entries = [] } = apiData;
    return {
      ...apiData,
      entries: entries.map((entry: any) => ({
        ...entry,
        approverName: entry.approverId ? (userMap[entry.approverId] || "") : "",
      })),
    };
  };

  const reportPresets = useMemo(() => {
    const defaultPresets = getPresets(reportId);
    if (!reportDef?.requiresClient || !reportDef?.requiresDateRange || !billingPeriod) {
      return defaultPresets;
    }

    const mode = normalizeBillingPeriodMode(billingPeriod.billingPeriodMode ?? billingPeriod.BillingPeriodMode);
    if (mode === "unset") return defaultPresets;

    const ranges = [
      {
        label: mode === "monthly" ? "Previous Month" : "Previous Billing Period",
        range: billingPeriod.previousPeriod ?? billingPeriod.PreviousPeriod,
      },
      {
        label: mode === "monthly" ? "Current Month" : "Current Billing Period",
        range: billingPeriod.currentPeriod ?? billingPeriod.CurrentPeriod,
      },
      {
        label: mode === "monthly" ? "Next Month" : "Next Billing Period",
        range: billingPeriod.nextPeriod ?? billingPeriod.NextPeriod,
      },
    ];

    const billingPresets = ranges
      .map(({ label, range }) => {
        const start = parseApiDateAsLocal(range?.startDate ?? range?.StartDate);
        const end = parseApiDateAsLocal(range?.endDate ?? range?.EndDate);
        return start && end ? { label, start, end } : null;
      })
      .filter((item): item is { label: string; start: Date; end: Date } => Boolean(item));

    return billingPresets.length ? billingPresets : defaultPresets;
  }, [billingPeriod, reportDef, reportId]);

  const runReport = async ({
    overrideDocId = null,
    overrideStartDate = null,
    overrideEndDate = null,
    overrideInvoicedOnly = invoicedOnly,
    preserveResultOnFailure = false,
  }: {
    overrideDocId?: number | null;
    overrideStartDate?: Date | null;
    overrideEndDate?: Date | null;
    overrideInvoicedOnly?: boolean;
    preserveResultOnFailure?: boolean;
  } = {}) => {
    const hasAccess = captureAccess();
    if (reportKey === "timeEntryDetails" && !canUseTimeEntryDetails) {
      setToast({ message: reportOperationFailure({ status: 403 }, "Report access denied."), type: "error" });
      return false;
    }
    if (reportKey === "invoice") {
      setReceiptSummary(null);
      setShowUninvoicedTimeWarning(false);
      setSuggestedInvoiceCustomFields([]);
      uninvoicedTimeCheckSeqRef.current += 1;
    }

    const effectiveStartDate = overrideStartDate || startDate;
    const effectiveEndDate = overrideEndDate || endDate;
    const effectiveDocId =
      overrideDocId !== null && overrideDocId !== undefined
        ? Number(overrideDocId)
        : Number(selectedDocId || 0);

    if (reportDef?.requiresClient && !clientId) {
      setToast({ message: "Please select a client.", type: "error" });
      return false;
    }

    if (requiresExistingInvoiceSelection(reportKey)) {
      if (!existingDocs?.length) {
        setToast({ message: "No invoices are available for this client and date range.", type: "error" });
        return false;
      }

      if (!effectiveDocId) {
        setToast({ message: `Please select an invoice before running the ${reportDef?.label || "report"}.`, type: "error" });
        return false;
      }
    }

    const errs = validate(clientId, effectiveStartDate, effectiveEndDate);
    if (errs.length) {
      setToast({ message: errs.join(" "), type: "error" });
      return false;
    }

    const sequence = ++reportRunSequence.current;
    const context = executionContextRef.current;
    const isCurrent = () => hasAccess() && sequence === reportRunSequence.current && context === executionContextRef.current;
    setIsRunning(true);
    setReportError("");

    try {
      const start = formatForApi(effectiveStartDate);
      const end = formatForApi(effectiveEndDate);

      const isOutstanding = reportKey === "outstanding";
      const selectedInvoiceId = usesInvoiceSelection(reportKey) ? effectiveDocId : 0;

      const meta = {
        reportId: reportKey,
        clientId: reportDef?.requiresClient ? Number(clientId) : null,
        startDate: start,
        endDate: end,
        invoiceId: selectedInvoiceId,
        outstandingId: isOutstanding ? effectiveDocId : 0,
      };

      if (isReportsV3Report(reportDef)) {
        const request: ReportExecuteRequestV3 = {
          definitionId: reportDef.definitionId,
          output: "preview",
          filters: {
            clientId: meta.clientId,
            startDate: meta.startDate,
            endDate: meta.endDate,
            invoiceId: selectedInvoiceId || undefined,
            ...(supportsIncludeSetting ? { invoicedOnly: overrideInvoicedOnly } : {}),
          },
        };
        const response = await api!.post<ReportExecuteResponseV3>("ReportsV3/execute", request);
        if (!isCurrent()) return false;
        const payload = toFormattedInvoicePayload(response);
        let effectiveInvoicedOnly: boolean | undefined;
        if (supportsIncludeSetting) {
          effectiveInvoicedOnly = requireEffectiveIncludeSetting(payload.metadata, overrideInvoicedOnly);
        }
        setResult({
          kind: "reportsV3",
          data: payload,
          meta: {
            ...meta,
            definitionId: response.definitionId || reportDef.definitionId,
            revision: reportDef.revision,
            definitionVersion: response.definitionVersion,
            schemaVersion: response.schemaVersion,
            ...(effectiveInvoicedOnly !== undefined ? { invoicedOnly: effectiveInvoicedOnly } : {}),
          },
        });
        return true;
      }

      let data;

      data = await fetchReportPayloadV2({
        reportId: meta.reportId,
        clientId: meta.clientId,
        startDate: meta.startDate,
        endDate: meta.endDate,
        invoiceId: meta.invoiceId,
        outstandingId: meta.outstandingId,
      });

      // Landman Status / Time Entry Details: flatten expenses and resolve approver names
      if (reportKey === "landmanStatus" || reportKey === "timeEntryDetails") {
        let userMap: Record<number, string> = {};
        try {
          const users = await api!.get("userinfo/lookup/users");
          if (Array.isArray(users)) {
            for (const u of users) {
              const uid = Number(u.userId);
              if (!isNaN(uid)) {
                userMap[uid] = `${u.firstName || ""} ${u.lastName || ""}`.trim();
              }
            }
          }
        } catch (error) {
          if (isReportAccessDenied(error)) throw error;
          console.error("Failed to load report approver names:", reportFailureMessage(error, "Approver names unavailable."));
        }
        data = resolveApproverNames(data, userMap);
        data = buildLandmanStatusReportData(data, approvalWorkflowEnabled);
      }

      if (reportKey === "landman") {
        data = normalizeLandmanPayees(data);
      }

      if (reportKey === "landmanPaymentSummary") {
        data = buildLandmanPaymentSummaryData(data);
      }

      if (reportKey === "myInvoice") {
        data = buildMyInvoiceReportData(data);
      }

      const { template: finalTemplate, data: finalData } = prepareReportOutput(reportDef?.template, data);
      if (!isCurrent()) return false;

      setResult({
        template: finalTemplate,
        data: finalData,
        meta,
      });

      return true;
    } catch (err: unknown) {
      if (!isCurrent()) return false;
      const message = reportOperationFailure(err, "Failed to run report.");
      if (!preserveResultOnFailure || isReportAccessDenied(err)) clearDisplayedReportState();
      setReportError(message);
      setToast({ message, type: "error" });
      return false;
    } finally {
      if (isCurrent()) setIsRunning(false);
    }
  };

  const onRun = async (e: any) => {
    e?.preventDefault();

    if (!runsFromToolbar(reportKey)) {
      const errs = validate(clientId, startDate, endDate);
      if (errs.length) {
        setToast({ message: errs.join(" "), type: "error" });
        return;
      }

      await loadInvoicesForSelection(clientId, startDate, endDate);
      return;
    }

    if (canRunUninvoicedDateRange(reportKey)) {
      setInvoiceSearchRangeActive(true);
      setSelectedDocId(0);
      setSelectedDocName("N/A");
      setLoadingDocId(0);
      await Promise.all([
        loadInvoicesForSelection(clientId, startDate, endDate, { forceRecent: false }),
        runReport({ overrideDocId: 0 }),
      ]);
      return;
    }

    await runReport();
  };

  const checkUninvoicedTimeForInvoice = async (doc: any) => {
    if (!api || reportKey !== "invoice") return;

    const nextSeq = uninvoicedTimeCheckSeqRef.current + 1;
    uninvoicedTimeCheckSeqRef.current = nextSeq;
    setShowUninvoicedTimeWarning(false);

    const docStart = formatForApi(toJsDate(doc?.startDate));
    const docEnd = formatForApi(toJsDate(doc?.endDate));
    const nextClientId = Number(clientId || 0);
    if (!nextClientId || !docStart || !docEnd) return;

    try {
      const preview = await api.get(`ReportsV2/InvoicePreview/${nextClientId}/${docStart}/${docEnd}`);
      if (uninvoicedTimeCheckSeqRef.current === nextSeq) {
        setShowUninvoicedTimeWarning(invoicePreviewHasUninvoicedTime(preview));
      }
    } catch (err: any) {
      if (uninvoicedTimeCheckSeqRef.current !== nextSeq) return;
      if (!isApiError(err) || err.code !== "NO_ELIGIBLE_INVOICE_ROWS") {
        console.error("❌ Error checking uninvoiced time:", reportFailureMessage(err, "Uninvoiced-time lookup failed."));
      }
      setShowUninvoicedTimeWarning(false);
      if (isReportAccessDenied(err)) setToast({ message: reportOperationFailure(err, "Failed to check invoice time."), type: "error" });
    }
  };

  const onLoadDoc = async (doc: any) => {
    const id = getInvoiceDocId(doc);
    const name = getInvoiceDocName(doc) || "N/A";
    const docStart = toJsDate(doc.startDate);
    const docEnd = toJsDate(doc.endDate);

    setLoadingDocId(id);

    try {
      const isInvoiceReport = reportKey === "invoice";
      if (!isInvoiceReport) {
        if (docStart) setStartDate(docStart);
        if (docEnd) setEndDate(docEnd);
        if (docStart && docEnd) setInvoiceSearchRangeActive(true);
        if (docStart) setBillingPeriodAnchorDate(docStart);
      }

      const ok = await runReport({
        overrideDocId: id,
        overrideStartDate: docStart || startDate,
        overrideEndDate: docEnd || endDate,
      });

      if (ok) {
        setSelectedDocId(id);
        setSelectedDocName(name);
        if (isInvoiceReport) {
          void checkUninvoicedTimeForInvoice(doc);
        }
      }
    } finally {
      setLoadingDocId(0);
    }
  };

  const confirmDeleteDoc = async () => {
    const isCurrent = captureAccess();
    if (!pendingDeleteDoc || !api) return;

    try {
      setIsDeletingDoc(true);
      const isInvoice = reportKey === "invoice";
      if (!canDeleteSelectedInvoice(reportKey)) return;

      const id = getInvoiceDocId(pendingDeleteDoc);
      if (!id) return;

      const path = isInvoice
        ? `ReportsV2/DeleteInvoice/${id}`
        : `ReportsV2/DeleteOutstandingInvoice/${id}`;

      await api.delete(path);
      if (!isCurrent()) return;
      await loadInvoicesForSelection(clientId, startDate, endDate, {
        forceRecent: reportKey === "invoice" && !invoiceSearchRangeActive,
      });
      if (!isCurrent()) return;

      if (reportKey === "invoice") {
        await loadSuggestedInvoiceName(clientId);
        if (!isCurrent()) return;
      }

      if (Number(selectedDocId) === Number(id)) {
        setSelectedDocId(0);
        setSelectedDocName("N/A");
        setResult(null);
        setReceiptSummary(null);
      }

      setPendingDeleteDoc(null);
      setToast({ message: "Invoice deleted.", type: "success" });
    } catch (e: any) {
      if (!isCurrent()) return;
      console.error("❌ Delete doc failed:", reportFailureMessage(e, "Invoice deletion failed."));
      setToast({ message: reportOperationFailure(e, "Failed to delete invoice."), type: "error" });
    } finally {
      setIsDeletingDoc(false);
    }
  };

  const handleDownloadReceipts = async () => {
    const isCurrent = captureAccess();
    if (
      !result?.meta?.clientId ||
      !result?.meta?.startDate ||
      !result?.meta?.endDate
    ) {
      return;
    }

    setIsDownloadingReceipts(true);

    try {
      const invoiceId = Number(result?.meta?.invoiceId || 0);
      const fileName =
        receiptSummary?.fileName ||
        (isGeneratedInvoiceMeta(result.meta)
          ? `invoice-${invoiceId}-receipts.zip`
          : `receipts-${result.meta.startDate}-${result.meta.endDate}.zip`);

      const path = isGeneratedInvoiceMeta(result.meta)
        ? `ReportsV2/DownloadReceiptsZip/${invoiceId}`
        : `ReportsV2/DownloadReceiptsZipPreview/${result.meta.clientId}/${result.meta.startDate}/${result.meta.endDate}`;

      await downloadBlob(path, fileName);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error("❌ Receipt download failed:", reportFailureMessage(err, "Receipt download failed."));
      setToast({ message: reportOperationFailure(err, "Failed to download receipts."), type: "error" });
    } finally {
      setIsDownloadingReceipts(false);
    }
  };

  const openGenerateInvoiceModal = async () => {
    const isCurrent = captureAccess();
    const [suggested, suggestedFields] = await Promise.all([
      loadSuggestedInvoiceName(clientId),
      loadSuggestedInvoiceCustomFields(clientId),
    ]);
    if (!isCurrent()) return;

    setInvoiceNameDraft(suggested || "");
    setInvoiceCustomFieldDrafts(
      suggestedFields.map((field) => createSuggestedInvoiceCustomFieldDraft(field)),
    );
    setInvoiceReportOptions(normalizeInvoiceReportOptions(getInvoiceReportOptionsSource(result?.data)));
    setCustomFieldModalMode("generate");
    setShowInvoiceNameModal(true);
  };

  const updateCustomFieldDraft = (
    index: number,
    patch: Partial<InvoiceCustomFieldDraft>,
  ) => {
    setInvoiceCustomFieldDrafts((prev) => prev.map((field, i) => (
      i === index ? { ...field, ...patch } : field
    )));
  };

  const addCustomFieldDraft = (
    label = "",
    placement: InvoiceCustomFieldPlacement = "summary",
  ) => {
    setInvoiceCustomFieldDrafts((prev) => {
      if (prev.length >= MAX_INVOICE_CUSTOM_FIELDS) return prev;
      return [...prev, createBlankInvoiceCustomField(label, placement)];
    });
  };

  const addSuggestedCustomFieldDraft = (field: InvoiceCustomFieldDraft) => {
    setInvoiceCustomFieldDrafts((prev) => {
      if (prev.length >= MAX_INVOICE_CUSTOM_FIELDS) return prev;
      const key = invoiceCustomFieldSuggestionKey(field);
      if (prev.some((draft) => invoiceCustomFieldSuggestionKey(draft) === key)) return prev;
      return [...prev, createSuggestedInvoiceCustomFieldDraft(field)];
    });
  };

  const removeCustomFieldDraft = (index: number) => {
    setInvoiceCustomFieldDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const moveCustomFieldDraft = (index: number, direction: -1 | 1) => {
    setInvoiceCustomFieldDrafts((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const closeCustomFieldModal = () => {
    if (isGeneratingInvoice || isSavingCustomFields || isLoadingCustomFields || isRunning) return;
    setShowInvoiceNameModal(false);
  };

  const openRunSettingsModal = () => {
    setDraftInvoicedOnly(invoicedOnly);
    setCustomFieldModalMode("run");
    setShowInvoiceNameModal(true);
  };

  const applyRunSettings = async () => {
    if (!supportsIncludeSetting || isRunning) return;
    setRunSettings({ context: settingsContext, invoicedOnly: draftInvoicedOnly });
    if (result?.kind === "reportsV3") {
      const succeeded = await runReport({
        overrideStartDate: parseApiDateAsLocal(result.meta.startDate),
        overrideEndDate: parseApiDateAsLocal(result.meta.endDate),
        overrideInvoicedOnly: draftInvoicedOnly,
        preserveResultOnFailure: true,
      });
      if (!succeeded) return;
    }
    setShowInvoiceNameModal(false);
  };

  const openEditCustomFieldsModal = async () => {
    const isCurrent = captureAccess();
    const invoiceId = Number(result?.meta?.invoiceId || 0);
    if (!api || !invoiceId) return;

    setIsLoadingCustomFields(true);
    try {
      const settings = await api.get<InvoiceReportSettings>(`ReportsV2/Invoice/${invoiceId}/Settings`);
      if (!isCurrent()) return;
      setInvoiceCustomFieldDrafts(normalizeInvoiceCustomFields(settings?.customFields));
      setInvoiceReportOptions(normalizeInvoiceReportOptions(settings?.reportOptions));
      setCustomFieldModalMode("edit");
      setShowInvoiceNameModal(true);
    } catch (err: any) {
      if (!isCurrent()) return;
      if (isNotFoundApiError(err)) {
        setInvoiceCustomFieldDrafts(normalizeInvoiceCustomFields(getInvoiceCustomFieldsSource(result?.data)));
        setInvoiceReportOptions(normalizeInvoiceReportOptions(getInvoiceReportOptionsSource(result?.data)));
        setCustomFieldModalMode("edit");
        setShowInvoiceNameModal(true);
        return;
      }
      setToast({ message: reportOperationFailure(err, "Failed to load invoice settings."), type: "error" });
    } finally {
      setIsLoadingCustomFields(false);
    }
  };

  const refreshReportsV3Result = async (invoiceId: number) => {
    const isCurrent = captureAccess();
    if (!api || !isReportsV3Report(reportDef) || !result?.meta?.definitionId) {
      return null;
    }

    const response = await api.post<ReportExecuteResponseV3>("ReportsV3/execute", {
      definitionId: result.meta.definitionId,
      output: "preview",
      filters: {
        clientId: result.meta.clientId,
        startDate: result.meta.startDate,
        endDate: result.meta.endDate,
        invoiceId: invoiceId || undefined,
        ...(typeof result.meta.invoicedOnly === "boolean" ? { invoicedOnly: result.meta.invoicedOnly } : {}),
      },
    });
    if (!isCurrent()) return null;
    const payload = toFormattedInvoicePayload(response);
    if (typeof result.meta.invoicedOnly === "boolean") {
      requireEffectiveIncludeSetting(payload.metadata, result.meta.invoicedOnly);
    }
    const nextResult = {
      kind: "reportsV3",
      data: payload,
      meta: {
        ...result.meta,
        invoiceId,
        definitionId: response.definitionId || result.meta.definitionId,
        definitionVersion: response.definitionVersion,
        schemaVersion: response.schemaVersion,
      },
    };
    setResult(nextResult);
    return nextResult;
  };

  const handlePrintClick = async () => {
    const isCurrent = captureAccess();
    if (!canAccessResult) {
      setToast({ message: reportOperationFailure({ status: 403 }, "Report access denied."), type: "error" });
      return;
    }
    if (!result || !canPrintPdf) return;

    try {
      setIsPrinting(true);
      if (result.kind === "reportsV3") {
        if (isInvoicePreviewMeta(result.meta)) {
          await openGenerateInvoiceModal();
          return;
        }
        setToast({ message: "Preparing PDF. This can take a few seconds.", type: "success" });
        await exportReportsV3Pdf({
          payload: result.data as FormattedInvoicePayload,
          meta: result.meta,
          fileName: result.meta?.fileName || "formatted-invoice.pdf",
          authToken: token!, tenantId: activeTenantId!, userId: user!.userId, getToken, isCurrent,
        });
        return;
      }

      const sortedData = applySortToData(result.data);
      if (result?.meta?.reportId !== "invoice") {
        setToast({ message: "Preparing PDF. This can take a few seconds.", type: "success" });
        await startPdfExport({
          template: result.template,
          meta: result.meta,
          data: sortedData,
          authToken: token!, tenantId: activeTenantId!, userId: user!.userId, getToken, isCurrent,
        });
        return;
      }

      const currentInvoiceId = Number(result?.meta?.invoiceId || 0);

      if (currentInvoiceId > 0) {
        setToast({ message: "Preparing PDF. This can take a few seconds.", type: "success" });
        await startPdfExport({
          template: result.template,
          meta: result.meta,
          data: sortedData,
          authToken: token!, tenantId: activeTenantId!, userId: user!.userId, getToken, isCurrent,
        });
        return;
      }

      await openGenerateInvoiceModal();
    } catch (err: any) {
      console.error("❌ Print failed:", reportFailureMessage(err, "PDF preparation failed."));
      if (!isCurrent()) return;
      setToast({ message: reportFailureMessage(err, "Failed to prepare PDF."), type: "error" });
    } finally {
      setIsPrinting(false);
    }
  };

  // Apply active sort to data for export
  const applySortToData = (data: any) => {
    if (!activeSort?.key || !activeSort.rowsPath) return data;
    const arr = data[activeSort.rowsPath];
    if (!Array.isArray(arr)) return data;
    const sorted = [...arr].sort((a: any, b: any) => {
      const av = a[activeSort.key!] ?? '';
      const bv = b[activeSort.key!] ?? '';
      const numA = Number(av);
      const numB = Number(bv);
      if (!isNaN(numA) && !isNaN(numB)) {
        return activeSort.dir === 'asc' ? numA - numB : numB - numA;
      }
      const cmp = String(av).localeCompare(String(bv));
      return activeSort.dir === 'asc' ? cmp : -cmp;
    });
    return { ...data, [activeSort.rowsPath]: sorted };
  };

  const handleExcelExport = async () => {
    if (!canAccessResult) {
      setToast({ message: reportOperationFailure({ status: 403 }, "Report access denied."), type: "error" });
      return;
    }
    if (!canExportExcel) {
      setToast({ message: "Excel export requires the raw_excel_export subscription feature.", type: "error" });
      return;
    }
    const isCurrent = captureAccess();
    if (!result?.data) return;
    setIsExporting(true);
    try {
      const clientName = clients.find(
        (c: any) => String(c.clientId) === String(result.meta?.clientId),
      )?.businessName;

      if (result.kind === "reportsV3") {
        if (!token || !activeTenantId || !user?.userId) {
          throw new Error("Authentication context is not ready.");
        }
        const notices = await exportReportsV3Workbook({
          payload: result.data as FormattedInvoicePayload,
          meta: result.meta,
          fileName: buildExportFileName(reportDef?.label || "formatted-invoice", {
            clientName,
            startDate: result.meta?.startDate,
            endDate: result.meta?.endDate,
          }),
          authToken: token,
          tenantId: activeTenantId,
          userId: user.userId,
          getToken,
          isCurrent,
        });
        if (!isCurrent()) return;
        setToast({ message: `Export downloaded.${notices ? " See Export Info for foundation export notices." : ""}`, type: "success" });
        return;
      }

      if (!result?.template) return;
      if (!token || !activeTenantId || !user?.userId) throw new Error("Authentication context is not ready.");
      const exportApi = createApiClient({ baseUrl: getPdfServerUrl(), token, getToken, tenantId: activeTenantId, userId: user.userId });
      const permission = await exportApi.post<{ authorized: boolean }>("reports/authorize-export", buildRenderContext(result.meta, "excel", "v2"));
      if (permission?.authorized !== true) throw new Error("The report export was not authorized.");
      if (!isCurrent()) return;

      const isTimeDetailsReport = reportKey === "landmanStatus" || reportKey === "timeEntryDetails";
      const tierColumnFilter = isTimeDetailsReport && !hasFeature("client_ready_reports")
        ? (col: any) => !["totalExpenses", "approverName"].includes(col.key)
        : undefined;

      const columnFilter = (col: any) => {
        if (tierColumnFilter && !tierColumnFilter(col)) return false;
        return visibilityColumnFilter(col);
      };

      // Inject dynamic expense columns into the template for Excel export
      let exportTemplate = result.template;
      if (isTimeDetailsReport && result.data.expenseColumns?.length) {
        const expenseCols = result.data.expenseColumns.map((ec: any) => (
          { key: `expense_${ec.name}_qty`, label: `${ec.name} (qty)` }
        ));
        exportTemplate = JSON.parse(JSON.stringify(result.template));
        const sections = exportTemplate?.layout?.sections || [];
        for (const section of sections) {
          for (const block of section.blocks || []) {
            if (block.type === "table" && block.columns) {
              // Insert expense columns before totalExpenses
              const idx = block.columns.findIndex((c: any) => c.key === "totalExpenses");
              if (idx >= 0) {
                block.columns.splice(idx, 0, ...expenseCols);
              } else {
                block.columns.push(...expenseCols);
              }
              break;
            }
          }
        }
      }

      await exportToExcel({
        template: exportTemplate,
        data: applySortToData(result.data),
        fileName: buildExportFileName(reportDef?.label || "report", {
          clientName,
          startDate: result.meta?.startDate,
          endDate: result.meta?.endDate,
        }),
        columnFilter,
        isCurrent,
      });
      setToast({ message: "Export downloaded.", type: "success" });
    } catch (err: any) {
      console.error("Excel export failed:", reportFailureMessage(err, "Excel export failed."));
      if (!isCurrent()) return;
      setToast({ message: reportFailureMessage(err, "Excel export failed. Please try again."), type: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  const handleConfirmGenerateAndPrint = async () => {
    const isCurrent = captureAccess();
    if (!api || !result?.meta?.clientId) return;

    const invoiceName = String(invoiceNameDraft || "").trim();
    if (!invoiceName) {
      setToast({ message: "Invoice name is required.", type: "error" });
      return;
    }

    const fieldErrors = validateInvoiceCustomFields(invoiceCustomFieldDrafts);
    if (fieldErrors.length) {
      setToast({ message: fieldErrors[0], type: "error" });
      return;
    }
    const settings = buildInvoiceSettingsPayload(invoiceCustomFieldDrafts, invoiceReportOptions);

    setIsGeneratingInvoice(true);

    try {
      const generatedInvoiceId = await api.post(
        `ReportsV2/GenerateInvoice/${result.meta.clientId}`,
        {
          startDate: result.meta.startDate,
          endDate: result.meta.endDate,
          invoiceName,
          settings,
        },
      );

      if (!isCurrent()) return;
      const newInvoiceId = Number(generatedInvoiceId);
      if (!newInvoiceId) {
        throw new Error(
          "The invoice was generated, but no invoice id was returned.",
        );
      }

      setSelectedDocId(newInvoiceId);
      setSelectedDocName(invoiceName);

      setInvoiceSearchRangeActive(true);
      await loadInvoicesForSelection(
        result.meta.clientId,
        result.meta.startDate,
        result.meta.endDate,
      );
      if (!isCurrent()) return;
      await loadSuggestedInvoiceName(result.meta.clientId);
      if (!isCurrent()) return;
      await loadSuggestedInvoiceCustomFields(result.meta.clientId);
      if (!isCurrent()) return;

      const nextResult: any = result.kind === "reportsV3"
        ? await refreshReportsV3Result(newInvoiceId)
        : null;
      if (!isCurrent()) return;

      let printableResult: any = nextResult;
      if (!printableResult) {
        const refreshedData = await fetchReportPayloadV2({
          reportId: "invoice",
          clientId: result.meta.clientId,
          startDate: result.meta.startDate,
          endDate: result.meta.endDate,
          invoiceId: newInvoiceId,
          outstandingId: 0,
        });

        if (!isCurrent()) return;
        const { template: finalTemplate, data: finalData } = prepareReportOutput(reportDef?.template, refreshedData);
        printableResult = {
          template: finalTemplate,
          data: finalData,
          meta: {
            ...result.meta,
            invoiceId: newInvoiceId,
          },
        };
        setResult(printableResult);
      }

      setShowInvoiceNameModal(false);
      setToast({ message: "Invoice generated. Preparing PDF. This can take a few seconds.", type: "success" });

      if (printableResult.kind === "reportsV3") {
        await exportReportsV3Pdf({
          payload: printableResult.data as FormattedInvoicePayload,
          meta: printableResult.meta,
          fileName: printableResult.meta?.fileName || "invoice.pdf",
          authToken: token!, tenantId: activeTenantId!, userId: user!.userId, getToken, isCurrent,
        });
      } else {
        await startPdfExport({
          template: printableResult.template,
          meta: printableResult.meta,
          data: printableResult.data,
          authToken: token!, tenantId: activeTenantId!, userId: user!.userId, getToken, isCurrent,
        });
      }
    } catch (err: any) {
      console.error("❌ Generate + print failed:", reportFailureMessage(err, "Invoice generation failed."));
      if (!isCurrent()) return;
      setToast({ message: typeof err.code === "string" && err.code.startsWith("RENDER_")
        ? reportFailureMessage(err, "Failed to print the generated invoice.")
        : reportOperationFailure(err, "Failed to generate and print invoice."), type: "error" });
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleSaveCustomFields = async () => {
    const isCurrent = captureAccess();
    if (!api || !result?.meta?.invoiceId) return;

    const fieldErrors = validateInvoiceCustomFields(invoiceCustomFieldDrafts);
    if (fieldErrors.length) {
      setToast({ message: fieldErrors[0], type: "error" });
      return;
    }

    const invoiceId = Number(result.meta.invoiceId);
    const settings = buildInvoiceSettingsPayload(invoiceCustomFieldDrafts, invoiceReportOptions);
    setIsSavingCustomFields(true);

    try {
      const savedSettings = await api.put<InvoiceReportSettings>(
        `ReportsV2/Invoice/${invoiceId}/Settings`,
        settings,
      );
      if (!isCurrent()) return;
      setInvoiceCustomFieldDrafts(normalizeInvoiceCustomFields(savedSettings?.customFields));
      setInvoiceReportOptions(normalizeInvoiceReportOptions(savedSettings?.reportOptions));
      if (result.kind === "reportsV3") {
        await refreshReportsV3Result(invoiceId);
        if (!isCurrent()) return;
      } else {
        const refreshedData = await fetchReportPayloadV2({
          reportId: "invoice",
          clientId: result.meta.clientId,
          startDate: result.meta.startDate,
          endDate: result.meta.endDate,
          invoiceId,
          outstandingId: 0,
        });
        if (!isCurrent()) return;
        const refreshedReportData = (refreshedData && typeof refreshedData === "object") ? refreshedData : {};
        const { template: finalTemplate, data: finalData } = prepareReportOutput(reportDef?.template, {
          ...refreshedReportData,
          settings: savedSettings,
          customFields: savedSettings?.customFields,
          reportOptions: savedSettings?.reportOptions,
        });

        setResult({
          template: finalTemplate,
          data: finalData,
          meta: result.meta,
        });
      }
      setShowInvoiceNameModal(false);
      setToast({ message: "Invoice settings updated.", type: "success" });
    } catch (err: any) {
      if (!isCurrent()) return;
      setToast({ message: reportOperationFailure(err, "Failed to save invoice settings."), type: "error" });
    } finally {
      setIsSavingCustomFields(false);
    }
  };

  useEffect(() => {
    if (!ready || !api) return;
    loadClients();
  }, [ready, api, loadClients, catalogReload]);

  useEffect(() => {
    if (!ready || !api) return;
    void loadBillingPeriod();
  }, [ready, api, loadBillingPeriod]);

  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);

  useEffect(() => {
    setResult(null);
    setReceiptSummary(null);
    setSelectedDocId(0);
    setSelectedDocName("N/A");
    setLoadingDocId(0);
    setIsLoadingInvoices(false);
    setPendingDeleteDoc(null);
    setBillingPeriodAnchorDate(startDateRef.current);
    setInvoiceSearchRangeActive(false);
    setShowUninvoicedTimeWarning(false);
    uninvoicedTimeCheckSeqRef.current += 1;
    setHasRecentInvoiceHistory(false);
    setInvoiceRangeSearchMissed(false);
  }, [reportId]);

  useEffect(() => {
    if (!ready || !api || !reportKey) return;

    if (!usesInvoiceSelection(reportKey) || !clientId) {
      setExistingDocs([]);
      setSuggestedInvoiceName("");
      setSuggestedInvoiceCustomFields([]);
      setShowUninvoicedTimeWarning(false);
      uninvoicedTimeCheckSeqRef.current += 1;
      return;
    }

    const useRecentFallback = reportKey === "invoice" && !invoiceSearchRangeActive;
    loadInvoicesForSelection(clientId, startDate, endDate, {
      forceRecent: useRecentFallback,
    });
    if (reportKey === "invoice") {
      loadSuggestedInvoiceName(clientId);
      loadSuggestedInvoiceCustomFields(clientId);
    } else {
      setSuggestedInvoiceName("");
      setSuggestedInvoiceCustomFields([]);
    }
  }, [
    ready,
    api,
    reportKey,
    clientId,
    startDate,
    endDate,
    invoiceSearchRangeActive,
    loadInvoicesForSelection,
    loadSuggestedInvoiceName,
    loadSuggestedInvoiceCustomFields,
  ]);

  useEffect(() => {
    if (!ready || !api || !result || result?.meta?.reportId !== "invoice") {
      setReceiptSummary(null);
      return;
    }

    const isCurrent = captureAccess();
    let disposed = false;
    const load = async () => {
      setLoadingReceiptSummary(true);

      try {
        const invoiceId = Number(result?.meta?.invoiceId || 0);
        const path = isGeneratedInvoiceMeta(result.meta)
          ? `ReportsV2/GetReceiptBundleSummary/${invoiceId}`
          : `ReportsV2/GetReceiptBundleSummaryPreview/${result.meta.clientId}/${result.meta.startDate}/${result.meta.endDate}`;
        const summary = await api.get(path);
        if (disposed || !isCurrent()) return;
        setReceiptSummary(summary || null);
      } catch (err: any) {
        if (disposed || !isCurrent()) return;
        console.error("❌ Error loading receipt summary:", reportFailureMessage(err, "Receipt summary lookup failed."));
        setReceiptSummary(null);
        if (isReportAccessDenied(err)) setToast({ message: reportOperationFailure(err, "Failed to load receipt summary."), type: "error" });
      } finally {
        if (!disposed && isCurrent()) setLoadingReceiptSummary(false);
      }
    };

    load();
    return () => { disposed = true; };
  }, [ready, api, result, captureAccess, reportOperationFailure]);

  useEffect(() => {
    if (reportKey === "invoice") return;
    if (!selectedDocId || !existingDocs.length || !startDate || !endDate) return;

    const selectedDoc = existingDocs.find((x: any) => {
      return getInvoiceDocId(x) === Number(selectedDocId);
    });

    if (!selectedDoc) return;

    const docStart = formatForApi(toJsDate(selectedDoc.startDate));
    const docEnd = formatForApi(toJsDate(selectedDoc.endDate));
    const currentStart = formatForApi(startDate);
    const currentEnd = formatForApi(endDate);

    if (docStart !== currentStart || docEnd !== currentEnd) {
      setSelectedDocId(0);
      setSelectedDocName("N/A");
    }
  }, [selectedDocId, existingDocs, startDate, endDate, reportKey]);

  const showExistingList = usesInvoiceSelection(reportKey);
  const invoiceSelectionRequired = requiresExistingInvoiceSelection(reportKey);
  const allowInvoiceDelete = canDeleteSelectedInvoice(reportKey);
  const historyCardTitle =
    reportKey === "outstanding" ? "Select Invoice" : "Report History";
  const historyMetaLabel =
    invoiceRangeSearchMissed
      ? `No invoice found for the selected date range. Showing the last ${existingDocs.length} invoice${existingDocs.length === 1 ? "" : "s"} for this client; adjust the date range and try again if needed.`
      : reportKey === "invoice" && !invoiceSearchRangeActive
      ? `Showing the last ${existingDocs.length} invoice${existingDocs.length === 1 ? "" : "s"} for this client`
      : `Showing ${existingDocs.length} invoice${existingDocs.length === 1 ? "" : "s"} for this client and date range`;

  const emptyLabel =
    invoiceRangeSearchMissed
      ? "No invoices found for this client and date range, and no recent invoices are available. Adjust the date range and try again."
      : reportKey === "outstanding"
        ? "No prior invoices are available for this client yet."
        : reportKey === "invoice" && invoiceSearchRangeActive
          ? hasRecentInvoiceHistory
            ? "No invoices found for this client and date range. Prior invoices exist outside this selected range."
            : "No invoices found for this client and date range."
          : invoiceSelectionRequired
            ? "No invoices found for this client and date range."
            : "No prior invoices found for this client yet.";

  const disableRun =
    isRunning ||
    !ready ||
    isLoadingInvoices ||
    loadingDocId > 0 ||
    (requiresExistingInvoiceSelection(reportKey) &&
      validate(clientId, startDate, endDate).length > 0);

  const toolbarActionLabel = getToolbarActionLabel(reportKey, existingDocs.length > 0);

  const hasResult = !!result;
  const isReportsV3Result = result?.kind === "reportsV3";
  const reportsV3Payload = isReportsV3Result ? result.data as FormattedInvoicePayload : null;
  const hasRenderableResult = hasResult && (
    isReportsV3Result
      ? hasReportsV3PreviewData(reportsV3Payload!)
      : reportResultHasRenderableData(result?.data)
  );
  const isInvoiceResult = result?.meta?.reportId === "invoice";
  const isInvoicePreviewResult = isInvoicePreviewMeta(result?.meta);
  const currentResultInvoiceId = Number(result?.meta?.invoiceId || 0);
  const canExportCurrentResult = canAccessResult && (isReportsV3Result
    ? canExportExcel && (reportsV3Payload?.schemaVersion !== "reports-v3.1" || nativeWorkbookSupport(reportsV3Payload).supported)
    : canExportExcel);
  const canPrintCurrentResult = canAccessResult && canPrintPdf;

  const printButtonLabel = !hasResult
    ? "Print Report"
    : result?.meta?.reportId === "invoice"
      ? currentResultInvoiceId > 0
        ? "Print Invoice"
        : "Generate & Print Invoice"
      : "Print Report";

  if (!reportsReady) {
    return (
      <div className="main-articles reports-articles">
        <div className={cssStyles['reports-shell']}>
          <div className={`${cssStyles['reports-card']} ${cssStyles['reports-toolbar-card']}`}>
            <div className={cssStyles['reports-card-header']}>
              <div className={cssStyles['reports-skeleton-title']} />
            </div>
            <div className={cssStyles['reports-card-body']}>
              <div className={cssStyles['reports-skeleton-grid']}>
                <div className={cssStyles['reports-skeleton-field']}>
                  <div className={cssStyles['reports-skeleton-label']} />
                  <div className={cssStyles['reports-skeleton-input']} />
                </div>
                <div className={cssStyles['reports-skeleton-field']}>
                  <div className={cssStyles['reports-skeleton-label']} />
                  <div className={cssStyles['reports-skeleton-input']} />
                </div>
                <div className={cssStyles['reports-skeleton-field']}>
                  <div className={cssStyles['reports-skeleton-label']} />
                  <div className={cssStyles['reports-skeleton-input']} />
                </div>
                <div className={cssStyles['reports-skeleton-field']}>
                  <div className={cssStyles['reports-skeleton-btn']} />
                </div>
              </div>
            </div>
          </div>
          {tokenError && (
            <div className={cssStyles.tokenError}>
              {String(tokenError)}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!reportDef) {
    return (
      <div className="main-articles reports-articles">
        <div className={cssStyles['reports-shell']}>
          <div className={`${cssStyles['reports-card']} ${cssStyles['reports-toolbar-card']}`}>
            <div className={cssStyles['reports-card-header']}>
              <h2 className={cssStyles['reports-card-header-title']}>Reports</h2>
            </div>
            <div className={cssStyles['reports-card-body']}>
              <div className={cssStyles['reports-empty-state']}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                  <path d="M9 17H7A5 5 0 0 1 7 7h2m6 10h2a5 5 0 0 0 0-10h-2M8 12h8" />
                </svg>
                <div className={cssStyles['reports-empty-title']}>No reports available</div>
                <div className={cssStyles['reports-empty-subtitle']} role={catalogFailure ? "alert" : "status"}>
                  {catalogFailure?.message || "There are no reports available for your current role and subscription features."}
                </div>
                <button type="button" className={cssStyles['reports-btn-secondary']} onClick={() => setCatalogReload(value => value + 1)}>Refresh access</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>

      <div className="main-articles reports-articles">
        <div className={cssStyles['reports-shell']}>
          {catalogFailure && <div className={cssStyles['reports-error']} role="alert">
            {catalogFailure.message}{" "}
            <button type="button" className={cssStyles['reports-btn-secondary']} onClick={() => setCatalogReload(value => value + 1)}>Refresh access</button>
          </div>}
          {reportError && (
            <div className={`no-print ${cssStyles['reports-error']}`} role="alert">
              {reportError}
            </div>
          )}

          <form onSubmit={onRun} className="no-print">
            <div className={`${cssStyles['reports-card']} ${cssStyles['reports-toolbar-card']}`}>
              <div className={`${cssStyles['reports-card-header']} ${supportsIncludeSetting ? cssStyles['reports-card-header--settings'] : ""}`}>
                <h2 className={cssStyles['reports-card-header-title']}>Report Settings</h2>
                {supportsIncludeSetting && (
                  <button
                    type="button"
                    className={`${cssStyles['reports-icon-btn']} ${cssStyles['reports-card-settings']}`}
                    onClick={openRunSettingsModal}
                    disabled={isRunning || isExporting || isPrinting}
                    aria-label="Edit Report Settings"
                    title="Edit Report Settings"
                    aria-haspopup="dialog"
                    aria-expanded={showInvoiceNameModal && customFieldModalMode === "run"}
                  >
                    <GearIcon />
                  </button>
                )}
              </div>
              <div className={cssStyles['reports-card-body']}>
              <div
                className={`${cssStyles['reports-toolbar-grid']} ${
                  reportDef?.requiresClient
                    ? cssStyles['reports-toolbar-grid--with-client']
                    : cssStyles['reports-toolbar-grid--no-client']
                }`}
              >
                <div className={cssStyles['reports-field']}>
                  <label className={cssStyles['reports-label']} htmlFor="reportType">
                    Report Type
                  </label>
                  <select
                    id="reportType"
                    className={cssStyles['reports-select']}
                    value={reportId}
                    onChange={(e: any) => setReportId(e.target.value)}
                  >
                    {visibleReports.map((r: any) => (
                      <option key={r.id} value={r.id}>
                        {getReportOptionLabel(r)}
                      </option>
                    ))}
                  </select>
                </div>

                {reportDef?.requiresClient && (
                  <div className={cssStyles['reports-field']}>
                    <label className={cssStyles['reports-label']} htmlFor="clientSelect">
                      Select Client
                    </label>

                    <SearchableSelect
                      id="clientSelect"
                      className={cssStyles['reports-select']}
                      options={clients.map((c: any) => ({ value: c.clientId, label: c.businessName }))}
                      value={clientId ? Number(clientId) : null}
                      onChange={(val) => {
                        const nextClientId = val ? String(val) : "";
                        if (nextClientId === clientId) return;
                        clearDisplayedReportState();
                        setClientId(nextClientId);
                        setBillingPeriod(null);
                        setBillingPeriodAnchorDate(startDate);
                        setInvoiceSearchRangeActive(false);
                        setHasRecentInvoiceHistory(false);
                        setInvoiceRangeSearchMissed(false);
                        setSuggestedInvoiceName("");
                        setSuggestedInvoiceCustomFields([]);
                      }}
                      placeholder="Search clients..."
                    />
                  </div>
                )}

                <div>
                  <div className={cssStyles['reports-preset-row']}>
                    {reportPresets.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        className={cssStyles['reports-preset-chip']}
                        onClick={() => {
                          clearDisplayedReportState();
                          setStartDate(p.start);
                          setEndDate(p.end);
                          setInvoiceSearchRangeActive(true);
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <ReportsDateRangeField
                    startDate={startDate}
                    endDate={endDate}
                    onChange={({ from, to }: any) => {
                      clearDisplayedReportState();
                      setStartDate(from);
                      setEndDate(to);
                      setInvoiceSearchRangeActive(true);
                      if (from) setBillingPeriodAnchorDate(from);
                    }}
                    disabled={isRunning || loadingDocId > 0}
                  />
                </div>

                <div className={cssStyles['reports-run-cell']}>
                  <button
                    type="submit"
                    className={`${cssStyles['reports-btn']} ${cssStyles['reports-run-btn']}`}
                    disabled={disableRun}
                  >
                    {isRunning
                      ? "Running..."
                      : isLoadingInvoices
                        ? "Loading..."
                        : toolbarActionLabel}
                  </button>
                </div>
              </div>
              {reportDef?.helpText && (
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '0', padding: '0 20px 12px', lineHeight: 1.4 }}>
                  {reportDef.helpText}
                </p>
              )}
              </div>
            </div>
          </form>

          {showExistingList && (
            <div className={`${cssStyles['reports-card']} no-print`}>
              <div className={cssStyles['reports-card-header']}>
                <h2 className={cssStyles['reports-card-header-title']}>{historyCardTitle}</h2>
              </div>
              <div className={cssStyles['reports-card-body']}>
                {existingDocs.length > 0 && (
                  <div className={cssStyles['reports-history-meta']}>
                    {loadingDocId > 0
                      ? "Loading selected invoice..."
                      : historyMetaLabel}
                  </div>
                )}

              {existingDocs.length === 0 ? (
                <div className={cssStyles['reports-empty']}>{emptyLabel}</div>
              ) : (
                <>
                  <div className={cssStyles['reports-history-columns']}>
                    <div>Invoice</div>
                    <div>Period Covered</div>
                    <div>Generated Date</div>
                    <div></div>
                  </div>

                  <div className={cssStyles['reports-history-list']}>
                    {existingDocs.map((doc: any) => {
                      const id = getInvoiceDocId(doc);
                      const name = getInvoiceDocName(doc);
                      const isLoaded = Number(selectedDocId) === Number(id);
                      const isLoading = Number(loadingDocId) === Number(id);

                      return (
                        <div
                          key={id}
                          className={`${cssStyles['reports-history-row']}${
                            isLoading
                              ? ` ${cssStyles['reports-history-row--loading']}`
                              : isLoaded
                                ? ` ${cssStyles['reports-history-row--loaded']}`
                                : ""
                          }`}
                          data-testid={`report-history-${id}`}
                        >
                          <div className={cssStyles['reports-history-cell']}>
                            <div className={cssStyles['reports-doc-cell']}>
                              <div className={cssStyles['reports-doc-content']}>
                                <button
                                  type="button"
                                  className={cssStyles['reports-doc-link']}
                                  onClick={() => onLoadDoc(doc)}
                                  disabled={isLoading || isDeletingDoc}
                                  data-testid={`load-report-${id}`}
                                >
                                  {name}
                                </button>

                                <div className={cssStyles['reports-doc-tags']}>
                                  {isLoading && (
                                    <span className={`${cssStyles['reports-tag']} ${cssStyles['reports-tag--loading']}`}>
                                      Loading...
                                    </span>
                                  )}

                                  {!isLoading && isLoaded && (
                                    <span className={`${cssStyles['reports-tag']} ${cssStyles['reports-tag--loaded']}`}>
                                      Loaded
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className={cssStyles['reports-history-cell']}>
                            {doc.periodCovered ||
                              formatPeriodLabel(doc.startDate, doc.endDate)}
                          </div>

                          <div className={cssStyles['reports-history-cell']}>
                            {formatDisplayDate(doc.generatedDate)}
                          </div>

                          <div className={cssStyles['reports-history-actions']}>
                            {allowInvoiceDelete && (
                              <button
                                type="button"
                                className={cssStyles['reports-icon-btn--danger']}
                                onClick={() => setPendingDeleteDoc(doc)}
                                disabled={isLoading || isDeletingDoc}
                                aria-label={`Delete ${name}`}
                                title="Delete invoice"
                              >
                                <TrashIcon />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedDocId > 0 && !loadingDocId && (
                    <div className={cssStyles['reports-loaded-note']}>
                      Loaded invoice: <strong>{selectedDocName}</strong>
                    </div>
                  )}
                </>
              )}
              </div>
            </div>
          )}

          {hasRenderableResult && (
            <div className={`${cssStyles['reports-card']} no-print`}>
              <div className={`${cssStyles['reports-card-header']} ${isInvoiceResult && currentResultInvoiceId > 0 ? cssStyles['reports-card-header--settings'] : ""}`}>
                <h2 className={cssStyles['reports-card-header-title']}>Report Actions</h2>
                {isInvoiceResult && currentResultInvoiceId > 0 && (
                  <button
                    type="button"
                    className={`${cssStyles['reports-icon-btn']} ${cssStyles['reports-card-settings']}`}
                    onClick={openEditCustomFieldsModal}
                    disabled={isLoadingCustomFields || isSavingCustomFields || isRunning || loadingDocId > 0 || isPrinting || isExporting}
                    aria-label="Edit Report Settings"
                    title="Edit Report Settings"
                    aria-haspopup="dialog"
                    aria-expanded={showInvoiceNameModal && customFieldModalMode === "edit"}
                    aria-busy={isLoadingCustomFields}
                  >
                    <GearIcon />
                  </button>
                )}
              </div>
              <div className={cssStyles['reports-actions-card']}>
              <div className={cssStyles['reports-actions-main']}>

                {isInvoiceResult ? (
                  loadingReceiptSummary ? (
                    <div className={cssStyles['reports-actions-copy']}>
                      Loading receipts…
                    </div>
                  ) : receiptSummary?.hasReceipts ? (
                    <div className={cssStyles['reports-asset-row']}>
                      <span className={cssStyles['reports-badge']}>
                        <ReceiptIcon />
                        {receiptSummary.count} receipt
                        {receiptSummary.count === 1 ? "" : "s"}
                      </span>

                      <button
                        type="button"
                        className={cssStyles['reports-btn-secondary']}
                        onClick={handleDownloadReceipts}
                        disabled={isDownloadingReceipts}
                      >
                        {isDownloadingReceipts
                          ? "Preparing download..."
                          : "Download All Receipts"}
                      </button>
                    </div>
                  ) : (
                    <div className={cssStyles['reports-actions-copy']}>
                      No receipts were submitted for this reporting period.
                    </div>
                  )
                ) : (
                  <div className={cssStyles['reports-actions-copy']}>
                    Ready to open a clean printable version of this report.
                  </div>
                )}
              </div>

              <div className={cssStyles['reports-actions-aside']}>
                {isInvoiceResult &&
                  isInvoicePreviewResult &&
                  suggestedInvoiceName && (
                    <div className={cssStyles['reports-suggestion']}>
                      Suggested invoice name:{" "}
                      <strong>{suggestedInvoiceName}</strong>
                    </div>
                  )}

                <div className={cssStyles['reports-actions-buttons']}>
                  {canPrintCurrentResult && (
                    <button
                      type="button"
                      className={`${cssStyles['reports-btn']} ${cssStyles['reports-print-btn']}`}
                      onClick={handlePrintClick}
                      disabled={isGeneratingInvoice || isPrinting || isRunning || loadingDocId > 0}
                    >
                      <PrintIcon />
                      {isGeneratingInvoice
                        ? "Generating..."
                        : isPrinting
                          ? "Preparing PDF..."
                          : printButtonLabel}
                    </button>
                  )}
                  {canExportCurrentResult && (
                    <button
                      type="button"
                      className={`${cssStyles['reports-btn']} ${cssStyles['reports-excel-btn']}`}
                      onClick={handleExcelExport}
                      disabled={!hasResult || isRunning || isExporting}
                      aria-label="Export to Excel"
                    >
                      <ExcelIcon />
                      {isExporting ? "Exporting..." : "Export to Excel"}
                    </button>
                  )}
                </div>
              </div>
              </div>
            </div>
          )}

          <div className={`${cssStyles['reports-card']} ${cssStyles['reports-preview-card']}`}>
            {isRunning && result && (
              <div className={cssStyles['reports-loading-overlay']}>
                <div className={cssStyles['reports-loading-text']}>Generating report…</div>
              </div>
            )}
            {result ? (() => {
              if (result.kind === "reportsV3") {
                const payload = result.data as FormattedInvoicePayload;
                if (!hasReportsV3PreviewData(payload)) {
                  return (
                    <div className={cssStyles['reports-preview-empty-state']}>
                      <div className={cssStyles['reports-preview-empty-icon']}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
                          <rect x="9" y="3" width="6" height="4" rx="1" stroke="#9ca3af" strokeWidth="1.5" />
                          <path d="M9 12h6M9 16h4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <p className={cssStyles['reports-preview-empty-title']}>No data for this period</p>
                      <p className={cssStyles['reports-preview-empty-subtitle']}>
                        The report definition returned no previewable data for this period.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className={cssStyles['reports-preview-render']}>
                    <ReportsV3Preview api={api} payload={payload} />
                  </div>
                );
              }
              const d = result.data || {};
              const hasData = reportResultHasRenderableData(d);
              if (!hasData) {
                return (
                  <div className={cssStyles['reports-preview-empty-state']}>
                    <div className={cssStyles['reports-preview-empty-icon']}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
                        <rect x="9" y="3" width="6" height="4" rx="1" stroke="#9ca3af" strokeWidth="1.5" />
                        <path d="M9 12h6M9 16h4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </div>
                    <p className={cssStyles['reports-preview-empty-title']}>No data for this period</p>
                    <p className={cssStyles['reports-preview-empty-subtitle']}>
                      There are no entries for the selected client and date range. Try a different period or client.
                    </p>
                  </div>
                );
              }
              const snapshotBacked = d.snapshotBacked === true;
              const snapshotCreatedAt = d.snapshotCreatedAt;
              const isGeneratedInvoice = result?.meta?.reportId === "invoice" && Number(result?.meta?.invoiceId || 0) > 0;
              return (
              <div className={cssStyles['reports-preview-render']}>
                {isGeneratedInvoice && showUninvoicedTimeWarning && (
                  <div className={`${cssStyles['reports-warning-banner']} no-print`} role="status">
                    There is uninvoiced time for this client and invoice period.
                  </div>
                )}
                {isGeneratedInvoice && (
                  <div
                    className={`${cssStyles['reports-snapshot-banner']} no-print`}
                    role="note"
                    data-testid="invoice-snapshot-banner"
                    data-snapshot-backed={snapshotBacked ? "true" : "false"}
                  >
                    {snapshotBacked ? (
                      <>
                        This invoice preserves the values used when it was generated.
                        {snapshotCreatedAt ? ` Snapshot created ${formatDisplayDate(snapshotCreatedAt)}.` : ""}
                      </>
                    ) : (
                      "This invoice was generated before snapshot support. Current values may be used until it is regenerated."
                    )}
                  </div>
                )}
                <div>
                  <ReportRenderer template={result.template} data={result.data} onSortChange={setActiveSort} />
                </div>
              </div>
              );
            })() : (
              <div className={cssStyles['reports-preview-empty']}>
                Select filters and click <strong>Run</strong> to generate a
                report.
              </div>
            )}
          </div>
        </div>
      </div>

      {showInvoiceNameModal && (
        <div style={styles.modalBackdrop} role="dialog" aria-modal="true" aria-labelledby="generateInvoiceTitle" data-testid="generate-invoice-modal">
          <div className={`${cssStyles['reports-modal-card']} ${cssStyles['reports-modal-card--scroll']}`}>
            <div className={cssStyles['reports-modal-header']} data-testid="reports-modal-header">
              <h2 className={cssStyles['reports-modal-title']} id="generateInvoiceTitle">
                {customFieldModalMode === "run" ? "Report settings" : customFieldModalMode === "generate" ? "Generate Invoice" : "Edit Report Settings"}
              </h2>
              {customFieldModalMode !== "run" && (
                <>
                  <div className={cssStyles['reports-modal-text']}>
                    {customFieldModalMode === "edit"
                      ? "Update the saved settings shown on this generated invoice."
                      : "Review invoice settings before locking time and generating the printable invoice."}
                  </div>
                  <div className={cssStyles['reports-modal-note']}>
                    {customFieldModalMode === "edit"
                      ? "Save updates custom fields and report layout options for this invoice."
                      : "Generated invoices preserve the values used when the invoice was created."}
                  </div>
                </>
              )}

              {customFieldModalMode === "generate" && (
                <>
                  <label htmlFor="invoiceNameDraft" className={cssStyles['reports-modal-label']}>
                    Invoice Name
                  </label>
                  <input
                    id="invoiceNameDraft"
                    type="text"
                    value={invoiceNameDraft}
                    onChange={(e: any) => setInvoiceNameDraft(e.target.value)}
                    className={cssStyles['reports-modal-input']}
                    autoFocus
                  />
                </>
              )}
            </div>

            <div className={cssStyles['reports-modal-body']} data-testid="reports-modal-body">
              {customFieldModalMode === "run" ? (
                <div className={cssStyles['reports-invoice-settings-section']}>
                  <label className={cssStyles['reports-checkbox-row']} htmlFor="reportsIncludeUninvoiced">
                    <input
                      id="reportsIncludeUninvoiced"
                      type="checkbox"
                      checked={!draftInvoicedOnly}
                      disabled={isRunning}
                      onChange={event => setDraftInvoicedOnly(!event.target.checked)}
                      aria-describedby="reportsIncludeHelp"
                    />
                    <span>Include approved time that hasn't been invoiced yet</span>
                  </label>
                  <p id="reportsIncludeHelp" className={cssStyles['reports-modal-text']}>Time already on an invoice is always included.</p>
                  {reportHelpUrl && <a className={cssStyles['reports-report-help']} href={reportHelpUrl} target="_blank" rel="noopener noreferrer">What does this mean?</a>}
                </div>
              ) : (
                <>
              <div className={cssStyles['reports-invoice-settings-section']}>
                <div className={cssStyles['reports-modal-label']}>Report Layout</div>
                <label className={cssStyles['reports-checkbox-row']} htmlFor="invoiceSplitSummariesByCounty">
                  <input
                    id="invoiceSplitSummariesByCounty"
                    type="checkbox"
                    checked={invoiceReportOptions.splitSummariesByCounty === true}
                    onChange={(event) => {
                      setInvoiceReportOptions((prev) => ({
                        ...prev,
                        splitSummariesByCounty: event.target.checked,
                      }));
                    }}
                    disabled={isGeneratingInvoice || isSavingCustomFields}
                  />
                  <span>
                    <strong>Split broker and prospect summaries by county</strong>
                    <small>Off by default. Landman details stay combined by landman.</small>
                  </span>
                </label>
              </div>

              <div className={cssStyles['reports-custom-fields-header']}>
                <div>
                  <div className={cssStyles['reports-modal-label']}>Invoice Custom Fields</div>
                  <div className={cssStyles['reports-custom-fields-help']}>
                    {customFieldModalMode === "generate"
                      ? "Fields are suggested from this client's previous invoices. Add or update values before generating."
                      : "Add invoice-level fields and choose where they appear on the report."}
                  </div>
                </div>
                <button
                  type="button"
                  className={cssStyles['reports-btn-ghost']}
                  onClick={() => addCustomFieldDraft()}
                  disabled={invoiceCustomFieldDrafts.length >= MAX_INVOICE_CUSTOM_FIELDS || isGeneratingInvoice || isSavingCustomFields}
                >
                  Add Field
                </button>
              </div>

              {customFieldModalMode === "generate" && suggestedInvoiceCustomFields.length > 0 && (
                <div className={cssStyles['reports-custom-suggestion-chips']} aria-label="Suggested custom fields">
                  {suggestedInvoiceCustomFields.map((field) => {
                    const isApplied = invoiceCustomFieldDrafts.some((draft) => (
                      invoiceCustomFieldSuggestionKey(draft) === invoiceCustomFieldSuggestionKey(field)
                    ));
                    return (
                      <button
                        key={invoiceCustomFieldSuggestionKey(field)}
                        type="button"
                        className={`${cssStyles['reports-custom-suggestion-chip']}${isApplied ? ` ${cssStyles['reports-custom-suggestion-chip--selected']}` : ""}`}
                        onClick={() => addSuggestedCustomFieldDraft(field)}
                        disabled={isApplied || invoiceCustomFieldDrafts.length >= MAX_INVOICE_CUSTOM_FIELDS || isGeneratingInvoice || isSavingCustomFields}
                        aria-pressed={isApplied}
                        title={field.value ? `${field.label}: ${field.value}` : field.label}
                      >
                        <span className={cssStyles['reports-custom-suggestion-chip-label']}>{field.label}</span>
                        <span className={cssStyles['reports-custom-suggestion-chip-placement']}>
                          {CUSTOM_FIELD_PLACEMENTS.find((item) => item.value === field.placement)?.label || field.placement}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className={cssStyles['reports-custom-fields-list']}>
                {invoiceCustomFieldDrafts.length === 0 ? (
                  <div className={cssStyles['reports-custom-fields-empty']}>
                    {customFieldModalMode === "generate"
                      ? "No custom field history found for this client."
                      : "No custom fields added."}
                  </div>
              ) : invoiceCustomFieldDrafts.map((field, index) => (
                <div className={cssStyles['reports-custom-field-row']} key={field.uid}>
                  <div className={cssStyles['reports-custom-field-grid']}>
                    <div className={cssStyles['reports-custom-field-control']}>
                      <label className={cssStyles['reports-modal-label']} htmlFor={`invoiceCustomFieldLabel${index}`}>
                        Label
                      </label>
                      <input
                        id={`invoiceCustomFieldLabel${index}`}
                        type="text"
                        value={field.label}
                        maxLength={MAX_INVOICE_CUSTOM_FIELD_LABEL_LENGTH}
                        onChange={(e) => updateCustomFieldDraft(index, { label: e.target.value })}
                        className={cssStyles['reports-modal-input']}
                      />
                    </div>
                    <div className={cssStyles['reports-custom-field-control']}>
                      <label className={cssStyles['reports-modal-label']} htmlFor={`invoiceCustomFieldValue${index}`}>
                        Value
                      </label>
                      <input
                        id={`invoiceCustomFieldValue${index}`}
                        type="text"
                        value={field.value}
                        maxLength={MAX_INVOICE_CUSTOM_FIELD_VALUE_LENGTH}
                        onChange={(e) => updateCustomFieldDraft(index, { value: e.target.value })}
                        className={cssStyles['reports-modal-input']}
                      />
                    </div>
                    <div className={cssStyles['reports-custom-field-control']}>
                      <label className={cssStyles['reports-modal-label']} htmlFor={`invoiceCustomFieldPlacement${index}`}>
                        Show In
                      </label>
                      <select
                        id={`invoiceCustomFieldPlacement${index}`}
                        value={field.placement}
                        onChange={(e) => updateCustomFieldDraft(index, { placement: e.target.value as InvoiceCustomFieldPlacement })}
                        className={cssStyles['reports-modal-input']}
                      >
                        {CUSTOM_FIELD_PLACEMENTS.map((placement) => (
                          <option key={placement.value} value={placement.value}>{placement.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={cssStyles['reports-custom-field-actions']}>
                    <button
                      type="button"
                      className={cssStyles['reports-custom-field-action-btn']}
                      onClick={() => moveCustomFieldDraft(index, -1)}
                      aria-label={`Move custom field ${field.label || index + 1} up`}
                      title="Move up"
                      disabled={index === 0 || isGeneratingInvoice || isSavingCustomFields}
                    >
                      <ArrowUpIcon />
                    </button>
                    <button
                      type="button"
                      className={cssStyles['reports-custom-field-action-btn']}
                      onClick={() => moveCustomFieldDraft(index, 1)}
                      aria-label={`Move custom field ${field.label || index + 1} down`}
                      title="Move down"
                      disabled={index === invoiceCustomFieldDrafts.length - 1 || isGeneratingInvoice || isSavingCustomFields}
                    >
                      <ArrowDownIcon />
                    </button>
                    <button
                      type="button"
                      className={cssStyles['reports-icon-btn--danger']}
                      onClick={() => removeCustomFieldDraft(index)}
                      aria-label={`Remove custom field ${field.label || index + 1}`}
                      title="Remove custom field"
                      disabled={isGeneratingInvoice || isSavingCustomFields}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
              </div>
                </>
              )}
            </div>

            <div className={`${cssStyles['reports-modal-actions']} ${cssStyles['reports-modal-footer']}`} data-testid="reports-modal-footer">
              <button
                type="button"
                className={cssStyles['reports-btn-ghost']}
                onClick={closeCustomFieldModal}
                disabled={isGeneratingInvoice || isSavingCustomFields || isRunning}
              >
                Cancel
              </button>
              <button
                type="button"
                className={cssStyles['reports-btn']}
                onClick={customFieldModalMode === "run" ? applyRunSettings : customFieldModalMode === "edit" ? handleSaveCustomFields : handleConfirmGenerateAndPrint}
                disabled={
                  customFieldModalMode === "run" ? isRunning :
                  customFieldModalMode === "edit"
                    ? isSavingCustomFields
                    : isGeneratingInvoice || !invoiceNameDraft.trim()
                }
              >
                {customFieldModalMode === "run" ? isRunning ? "Applying..." : "Apply" : customFieldModalMode === "edit"
                  ? isSavingCustomFields ? "Saving..." : "Save Settings"
                  : isGeneratingInvoice ? "Generating..." : "Generate & Print"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteDoc && (
        <div style={styles.modalBackdrop} role="dialog" aria-modal="true" aria-labelledby="deleteInvoiceTitle" data-testid="delete-invoice-modal">
          <div className={cssStyles['reports-modal-card']}>
            <h2 className={cssStyles['reports-modal-title']} id="deleteInvoiceTitle">Delete Invoice</h2>
            <div className={cssStyles['reports-modal-text']}>
              Are you sure you want to delete{" "}
              <strong>
                {pendingDeleteDoc.invoiceName ||
                  pendingDeleteDoc.reportName ||
                  "this invoice"}
              </strong>
              ? This will remove the saved invoice record.
            </div>

            <div className={cssStyles['reports-modal-actions']}>
              <button
                type="button"
                className={cssStyles['reports-btn-ghost']}
                onClick={() => setPendingDeleteDoc(null)}
                disabled={isDeletingDoc}
              >
                Cancel
              </button>
              <button
                type="button"
                className={cssStyles['reports-btn-danger']}
                onClick={confirmDeleteDoc}
                disabled={isDeletingDoc}
              >
                {isDeletingDoc ? "Deleting..." : "Delete Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};

function ReportsDateRangeField({
  startDate,
  endDate,
  onChange,
  disabled = false,
}: any) {
  const triggerRef = useRef<any>(null);
  const popoverRef = useRef<any>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [draftRange, setDraftRange] = useState({
    from: startDate,
    to: endDate,
  });
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [popoverPos, setPopoverPos] = useState({
    top: 0,
    left: 0,
    ready: false,
  });
  const [visibleMonth, setVisibleMonth] = useState(startDate || new Date());

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDraftRange({
        from: startDate,
        to: endDate,
      });
      setIsSelectingEnd(false);
      setVisibleMonth(startDate || new Date());
    }
  }, [startDate, endDate, isOpen]);

  const updatePopoverPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const top = rect.bottom + 4;
    const visualWidth =
      popoverRef.current?.getBoundingClientRect().width || REPORTS_CALENDAR_DEFAULT_WIDTH;
    const maxLeft = Math.max(
      REPORTS_CALENDAR_VIEWPORT_PADDING,
      window.innerWidth - visualWidth - REPORTS_CALENDAR_VIEWPORT_PADDING,
    );
    const desiredLeft = rect.left - 50;
    const left = Math.min(
      Math.max(REPORTS_CALENDAR_VIEWPORT_PADDING, desiredLeft),
      maxLeft,
    );

    setPopoverPos({
      top,
      left,
      ready: true,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen || isMobile) return;

    updatePopoverPosition();

    const handleReposition = () => {
      updatePopoverPosition();
    };

    window.addEventListener("resize", handleReposition);
    document.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      document.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (event: any) => {
      const target = event.target;

      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;

      setIsOpen(false);
      setDraftRange({
        from: startDate,
        to: endDate,
      });
      setIsSelectingEnd(false);
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, startDate, endDate]);

  const openPicker = () => {
    if (disabled) return;

    setDraftRange({
      from: startDate,
      to: endDate,
    });
    setIsSelectingEnd(false);
    setVisibleMonth(startDate || new Date());

    if (!isMobile && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 4,
        left: Math.max(REPORTS_CALENDAR_VIEWPORT_PADDING, rect.left - 50),
        ready: false,
      });
    } else {
      setPopoverPos((prev: any) => ({ ...prev, ready: true }));
    }

    setIsOpen(true);
  };

  const handleDayClick = (day: any, modifiers: any) => {
    if (disabled || modifiers.disabled) return;

    if (!isSelectingEnd) {
      setDraftRange({ from: day, to: undefined });
      setIsSelectingEnd(true);
      setVisibleMonth(day);
      return;
    }

    const first = draftRange?.from;
    if (!first) {
      setDraftRange({ from: day, to: undefined });
      setVisibleMonth(day);
      return;
    }

    const from = first <= day ? first : day;
    const to = first <= day ? day : first;

    const nextRange = { from, to };
    setDraftRange(nextRange);
    onChange(nextRange);
    setIsSelectingEnd(false);
    setIsOpen(false);
  };

  const calendar = (
    <div
      ref={popoverRef}
      className={`${cssStyles['la-rdp-wrap']} ${
        isMobile ? cssStyles['reports-mobile-calendar-card'] : cssStyles['reports-calendar-card']
      }`}
      role="dialog"
      aria-label="Select reporting period"
      data-testid="reporting-period-dialog"
      style={
        isMobile
          ? undefined
          : {
              top: popoverPos.top,
              left: popoverPos.left,
              visibility: popoverPos.ready ? "visible" : "hidden",
            }
      }
    >
      {isMobile && (
        <div className={cssStyles['reports-mobile-calendar-header']}>
          <div className={cssStyles['reports-mobile-calendar-title']}>Select Reporting Period</div>
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setDraftRange({
                from: startDate,
                to: endDate,
              });
              setIsSelectingEnd(false);
            }}
            className={cssStyles['reports-mobile-calendar-close']}
          >
            Close
          </button>
        </div>
      )}

      <div className={cssStyles['reports-calendar-hint']}>
        {isSelectingEnd ? "Select the end date." : "Select a start date."}
      </div>

      <DayPicker
        mode="range"
        numberOfMonths={1}
        selected={draftRange}
        month={visibleMonth}
        onMonthChange={setVisibleMonth}
        onDayClick={handleDayClick}
        showOutsideDays
      />
    </div>
  );

  return (
    <div className={cssStyles['reports-field']}>
      <label className={cssStyles['reports-label']} htmlFor="invoiceRange">
        Reporting Period
      </label>

      <button
        id="invoiceRange"
        ref={triggerRef}
        type="button"
        className={cssStyles['reports-range-trigger']}
        disabled={disabled}
        onClick={openPicker}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className={cssStyles['reports-range-trigger__value']}>
          {formatPeriodLabel(startDate, endDate) || "Select date range"}
        </span>

        <span className={cssStyles['reports-range-trigger__icon']} aria-hidden="true">
          <CalendarMiniIcon />
        </span>
      </button>

      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          isMobile ? (
            <div className={cssStyles['reports-mobile-calendar-backdrop']}>{calendar}</div>
          ) : (
            calendar
          ),
          document.body,
        )}
    </div>
  );
}

const Reports = () => {
  const authRequest = { ...loginRequest };
  const { user, activeTenantId, loading } = useUser();
  const { tier, hasFeature, loading: tierLoading } = useTier();
  const accessContext = JSON.stringify([activeTenantId, user?.userId, user?.roleId, loading, tierLoading, tier,
    ...["invoice_generation", "custom_report_templates", "client_ready_reports", "raw_excel_export", "approval_workflow"].map(key => hasFeature(key))]);

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <ReportsInner key={accessContext} />
    </MsalAuthenticationTemplate>
  );
};

const styles = {
  modalBackdrop: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    padding: 20,
    zIndex: 2000,
  },
};

export default Reports;